import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { CORE_STAFF_ROLES } from '../config/roleConfig';
import { useAccount } from '../context/AccountContext';
import secureApiService, { secureApi } from '../services/secureApi';
import axios from 'axios';
import '../styles/PropertiesManagementStyled.css';
import { formatCompactDate } from '../utils/dateFormatter';
import { getPlatformColor } from '../utils/platformUtils';
import { PUBLIC_LINK_PLATFORMS, isValidPublicLink, getDirectBookingUrl } from '../utils/publicBookingLinks';
import {
  AMENITY_GROUPS,
  AMENITY_DETAILS,
  PARKING_TYPES,
  KITCHEN_TYPES,
  COPYABLE_AMENITY_PATHS,
  readAmenity,
  writeAmenity
} from '../config/amenityCatalogue';
import { formatProgress } from '../utils/numberFormatter';
import AccountSwitcher from './common/AccountSwitcher';
import GuestGuideLinkCard from './guidebook/GuestGuideLinkCard';
import UnitHouseRulesCard from './guidebook/UnitHouseRulesCard';
import useLanguage from '../hooks/useLanguage';
import {
  EARLY_CHECKIN_FIELD_LABEL,
  EARLY_CHECKIN_OPTIONS,
  EARLY_CHECKIN_VALUES,
  EARLY_CHECKIN_HELP,
  EARLY_CHECKIN_POLICY_NOT_STATE_AR,
  EARLY_CHECKIN_TIME_LABEL,
  EARLY_CHECKIN_ERRORS,
  earlyCheckInRejection
} from '../config/earlyCheckInLabels';
import { formatTime } from '../utils/dateFormatter';
import UnitStatusChip from './UnitStatusChip';
import unitStatusApi from '../services/unitStatusApi';

/**
 * The catalogue display order. Blank is allowed and means "no preference" — it is sent as 0, which
 * is what the backend and the schema both default to. Mirrors the rule in `securePropertyRoutes.js`
 * (`PROPERTY_LISTING_ORDER_INVALID`); must match the backend.
 */
/**
 * The unit facts a guest compares on the public catalogue card. Blank is allowed everywhere and
 * means "not measured" — which is why a card omits a spec rather than printing 0.
 * Bounds mirror `securePropertyRoutes.js` (`PROPERTY_SPEC_INVALID`); must match the backend.
 */
const UNIT_SPECS = [
  { key: 'maxGuests', label: 'عدد الضيوف', min: 1, max: 50, hint: 'الحد الأقصى للنزلاء' },
  { key: 'bedrooms', label: 'غرف النوم', min: 0, max: 20, hint: '0 = استوديو' },
  { key: 'bathrooms', label: 'دورات المياه', min: 0, max: 20, hint: '' }
];

const isValidSpec = (value, { min, max }) => {
  if (value === undefined || value === null || String(value).trim() === '') return true;
  const n = Number(String(value).trim());
  return Number.isInteger(n) && n >= min && n <= max;
};

const isValidSortOrder = (value) => {
  if (value === undefined || value === null || String(value).trim() === '') return true;
  return /^\d{1,4}$/.test(String(value).trim()) && Number(value) <= 9999;
};

// Removed: getShortLinkBase() - no longer needed with YouTube-only approach

const GUIDEBOOK_SECTION_TEMPLATE = {
  onboardingVideo: '',  // YouTube URL only
  onboardingVideoPoster: '',  // Registered so normalize/save no longer wipe the model field
  arrival: {
    entryType: 'smartlock',
    entryCode: '',
    entryInstructionsAr: '',
    entryInstructionsEn: '',
    keyLocationAr: '',
    keyLocationEn: '',
    // Structured early check-in (D153). `null` = never set → inherit the portfolio
    // default (be/config/earlyCheckIn.js). Mirrors the backend template in
    // be/routes/securePropertyRoutes.js, which is the save's allow-list (a field not
    // registered there cannot be saved). Saves merge over stored values, so nothing omitted is wiped.
    earlyCheckInPolicy: null,
    earlyCheckInNotBefore: '',
    lateCheckOutPolicyAr: '',
    lateCheckOutPolicyEn: ''
  },
  // Ordered photo journey for the public arrival guide (tasks/unit-arrival-guide-page.md).
  // Mirrors the backend copy in be/routes/securePropertyRoutes.js — the server-owned allow-list.
  // This copy only supplies form defaults; the save merges over stored values field by field.
  //
  // ⚠️ `normalizeGuidebook` shallow-merges this object over the template, so when a property has
  // no stored guide the resulting `images` ALIASES this array. Never mutate it in place — every
  // handler below rebuilds the list immutably.
  arrivalGuide: {
    images: []
  },
  wifi: {
    networkName: '',
    password: '',
    instructionsAr: '',
    instructionsEn: ''
  },
  parking: {
    descriptionAr: '',
    descriptionEn: ''
  },
  climate: {
    acInstructionsAr: '',
    acInstructionsEn: '',
    heaterInstructionsAr: '',
    heaterInstructionsEn: ''
  },
  appliances: {
    kitchenNotesAr: '',
    kitchenNotesEn: '',
    washerNotesAr: '',
    washerNotesEn: '',
    tvNotesAr: '',
    tvNotesEn: ''
  },
  amenities: {
    poolInfoAr: '',
    gymInfoAr: '',
    familyInfoAr: ''
  },
  safety: {
    emergencyContacts: '',
    safetyNotesAr: ''
  },
  localTips: {
    supermarketsAr: '',
    restaurantsAr: '',
    mosquesAr: '',
    deliveryAppsAr: ''
  },
  extras: {
    extraBedsAr: '',
    linensAr: '',
    suppliesAr: ''
  },
  customNotes: {
    notesAr: '',
    notesEn: ''
  },
  waste: {
    descriptionAr: '',
    descriptionEn: ''
  },
  // Empty = the unit uses the company rules (be/models/GuestGuideDefaults.js).
  houseRulesOverride: {
    rulesAr: [],
    rulesEn: []
  }
};

const createDefaultGuidebook = () => ({
  version: 1,
  isPublished: false,
  sections: JSON.parse(JSON.stringify(GUIDEBOOK_SECTION_TEMPLATE))
});

const normalizeGuidebook = (guidebook) => {
  const base = createDefaultGuidebook();
  if (!guidebook) {
    return base;
  }

  const normalizedSections = {};
  Object.keys(GUIDEBOOK_SECTION_TEMPLATE).forEach((key) => {
    const templateValue = GUIDEBOOK_SECTION_TEMPLATE[key];
    const incomingValue = guidebook.sections?.[key];

    if (templateValue && typeof templateValue === 'object' && !Array.isArray(templateValue)) {
      normalizedSections[key] = {
        ...templateValue,
        ...(incomingValue || {})
      };
    } else {
      normalizedSections[key] = incomingValue ?? templateValue;
    }
  });

  return {
    version: guidebook.version || base.version,
    isPublished: guidebook.isPublished ?? base.isPublished,
    sections: normalizedSections
  };
};

/**
 * Property thumbnail for the list cards.
 * Falls back to a neutral placeholder when no photo is set OR the URL fails to load —
 * an empty photo is the normal state for a property that hasn't been photographed yet,
 * so it must read as deliberate rather than broken.
 */
/**
 * One field in «دليل العقار» whose subject is an amenity declared on the public tab.
 *
 * tasks/public-amenities-consolidation.md D3. The public tab answers DOES THE UNIT HAVE IT; this
 * tab only answers WHAT THE GUEST NEEDS TO KNOW. So the availability appears here as a read-only
 * badge and is never editable twice — otherwise the two tabs can disagree («لا يوجد مسبح» beside
 * "the pool is on the roof") and nothing downstream, least of all the AI that will read this guide,
 * can tell which one is true.
 *
 * Three states, and the third is the reason this is a component rather than a ternary:
 *
 *   available                      → badge «متاح», field shown.
 *   not available, field empty     → hidden. Nothing to say about an amenity the unit lacks.
 *   not available, field HAS TEXT  → shown, with a warning. NEVER hidden.
 *
 * That last case is the whole point. Hiding text that is still stored would leave it invisible to
 * ops and still readable by everything downstream — the exact contradiction this change removes,
 * only now unfixable through the UI. Ops is asked to resolve it instead.
 */
const AmenityGuideField = ({ label, amenityPath, amenities, value, className = 'col-md-4 mb-3', children }) => {
  const available = !!readAmenity(amenities, amenityPath);
  const hasContent = !!String(value || '').trim();

  if (!available && !hasContent) return null;

  return (
    <div className={className}>
      <label className="form-label d-flex align-items-center gap-2 flex-wrap">
        {/* dir="auto": an English label («… (English - اختياري)») would otherwise be reordered by
            the RTL page. */}
        <span dir="auto">{label}</span>
        {available ? (
          <span className="badge bg-light text-secondary fw-normal">متاح</span>
        ) : (
          <span className="badge bg-warning text-dark fw-normal">غير مُحدَّد كمرفق</span>
        )}
      </label>
      {children}
      {!available && hasContent && (
        <small className="text-danger d-block mt-1">
          هذا المرفق غير مُحدَّد في تبويب «الصفحة العامة». فعّله هناك أو احذف هذا النص.
        </small>
      )}
    </div>
  );
};

/**
 * Would any field in an amenity-gated guide section actually render?
 *
 * A section heading sits OUTSIDE its gated fields, so a card whose every field is gated can
 * collapse to a bare heading with nothing under it — which is what «التكييف والأجهزة» does on a
 * unit with no amenities ticked, and today that is 8 of 12 (`heating` and `kitchen` are ticked on
 * none at all). An empty card reads as a bug; a section that silently vanishes reads as lost work.
 * Both get an explicit empty state pointing at the tab that fills them.
 *
 * `pairs` is [[amenityPath, currentGuideValue], ...] — matching AmenityGuideField's own rule, so a
 * field holding text always counts as visible even when its amenity is switched off.
 */
const anyAmenityFieldVisible = (amenities, pairs) =>
  pairs.some(([path, value]) => !!readAmenity(amenities, path) || !!String(value || '').trim());

const PropertyThumb = ({ src, name }) => {
  const [failed, setFailed] = useState(false);
  const showImage = !!src && !failed;

  return (
    <div className={`property-card-thumb${showImage ? '' : ' property-card-thumb--empty'}`}>
      {showImage ? (
        <img src={src} alt={name || 'صورة العقار'} loading="lazy" onError={() => setFailed(true)} />
      ) : (
        <i className="bi bi-house" aria-hidden="true"></i>
      )}
    </div>
  );
};

const SecurePropertiesManagement = ({ mode = 'list' }) => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { t } = useLanguage();
  // Ops edit a unit's GUIDEBOOK only (founder decision 2026-10-04 — tasks/guest-guide-sections-and-print-sheet.md
  // G11). They get the «دليل العقار» tab and nothing else: no other tab, no «تحديث العقار», no delete.
  // The guidebook saves through its own endpoint, which already admits staff.
  const isGuidebookOnly = mode === 'edit' && (user?.role === 'ops' || user?.role === 'ops-manager');
  const { selectedAccount } = useAccount(); // Global account selection from Context
  const [properties, setProperties] = useState([]);
  // «حالة التشغيل» per unit, keyed by property id — loaded after the list (unitStatusApi.getMany).
  const [unitStatuses, setUnitStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active'); // 'active' | 'all' | 'deleted'

  // State for property creation/edit form
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: 'جدة',
    type: 'شقة',
    status: 'active',
    description: '',
    // Check-in message location fields (§0.3–0.4)
    buildingName: '',
    floor: '',
    unitNumber: '',
    mapsUrl: '',
    additionalCheckinNotesAr: '',
    checkinAutomationEnabled: false,
    checkoutAutomationEnabled: false,
    stayCheckAutomationEnabled: false,
    guestReviewAutomationEnabled: false,
    guestDisplayEnabled: false,
    checkInAssets: [],
    primaryOwnerId: '',
    // Public guest-facing listing URLs (tasks/property-public-booking-links.md)
    publicLinks: { airbnb: '', booking: '', gathern: '' },
    // Public catalogue placement (tasks/public-listings-page.md) — unpublished until asked for
    publicListing: { isPublished: false, sortOrder: 0 },
    // Unit facts a guest compares (tasks/public-listings-page.md step 6). Blank, never 0 — a spec
    // nobody has measured must stay absent so the card can omit it.
    specs: { maxGuests: '', bedrooms: '', bathrooms: '' },
    features: {
      directBookingEnabled: false,
      onlinePaymentEnabled: false,
      promoCodesEnabled: false
    },
    platformAccounts: {
      airbnb: {
        propertyId: '',
        listingName: '',
        calendarUrl: '',
        status: 'inactive',
        lastSync: null
      },
      booking: {
        propertyId: '',
        listingName: '',
        calendarUrl: '',
        status: 'inactive',
        lastSync: null
      }
    },
    gathernMapping: {
      propertyId: '',
      calendarUrl: '',
      exportUrl: '',
      listingName: '',
      status: 'pending',
      lastSync: null,
      lastSyncError: '',
      syncMetadata: {
        lastImportedBookings: 0,
        lastExportGenerated: null,
        importErrorCount: 0
      },
      connectedAt: null
    },
    channexMapping: {
      propertyId: '',
      roomTypes: [],
      status: 'unmapped',
      mappedAt: null
    },
    otaListings: {
      booking: {
        active: false,
        propertyId: '',
        authorizationStatus: 'not_requested',
        authRequestedAt: null,
        authCompletedAt: null,
        connectedAt: null,
        lastVerified: null
      },
      airbnb: {
        active: false,
        listingId: '',
        listingUrl: '',
        authorizationStatus: 'not_requested',
        authRequestedAt: null,
        authCompletedAt: null,
        connectedAt: null,
        lastVerified: null
      }
    },
    amenities: {
      wifi: {
        available: false,
        networkName: '',
        password: '',
        instructions: '',
        instructionsAr: ''
      },
      parking: {
        available: false,
        type: 'none',
        instructions: '',
        instructionsAr: ''
      },
      pool: {
        available: false,
        hours: '',
        location: '',
        locationAr: ''
      },
      gym: {
        available: false,
        hours: '',
        location: '',
        locationAr: ''
      },
      elevator: false,
      airConditioning: false,
      heating: false,
      washingMachine: false,
      dryer: false,
      kitchen: {
        available: false,
        type: 'none'
      },
      tv: false,
      workspace: false
    },
    bookingPageSettings: {
      primaryPhoto: '',
      gallery: [],
      publicTitle: '',
      description: '',
      descriptionEn: '',
      checkInTime: '15:00',
      checkOutTime: '11:00',
      houseRules: [],
      houseRulesEn: [],
      cancellationPolicy: {
        type: 'moderate',
        fullRefundDays: 14,
        partialRefundDays: 7,
        partialRefundPercent: 50
      },
      importantInformation: '',
      importantInformationEn: '',
      location: {
        coordinates: {
          lat: 0,
          lng: 0
        },
        neighborhood: ''
      }
    },
    guidebook: createDefaultGuidebook()
  });
  const [creating, setCreating] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [originalProperty, setOriginalProperty] = useState(null);
  const [syncStatus, setSyncStatus] = useState('idle');
  const [syncMessage, setSyncMessage] = useState('');
  const [baseRate, setBaseRate] = useState(220); // Default 220 SAR
  const [weekendIncrease, setWeekendIncrease] = useState(30); // Default 30%
  const [ownerUsers, setOwnerUsers] = useState([]);
  const [activeTab, setActiveTab] = useState(isGuidebookOnly ? 'guidebook' : 'basic'); // 'basic' | 'publicContent' | 'guidebook'
  const [availableRoomTypes, setAvailableRoomTypes] = useState([]); // Channex room types for multi-unit
  const [loadingRoomTypes, setLoadingRoomTypes] = useState(false);
  const [savingLinkedRoomType, setSavingLinkedRoomType] = useState(false); // Track linked room type save
  const [uploadingImage, setUploadingImage] = useState(false); // Track image upload state
  // Separate from `uploadingImage`: that flag is shared by the gallery and check-in widgets,
  // and a shared spinner puts the busy state on whichever widget the operator is not using.
  const [uploadingArrivalGuide, setUploadingArrivalGuide] = useState(false);
  // The guest link. Held separately from `formData` because the token is `select: false` and so
  // never arrives with the property — it is fetched on its own, and only for staff who open the tab.
  const [arrivalGuideLink, setArrivalGuideLink] = useState(null);
  // Read inside the debounced auto-save, which closes over an older render.
  const arrivalGuideLinkRef = useRef(null);
  arrivalGuideLinkRef.current = arrivalGuideLink;
  const [guidebookSaveStatus, setGuidebookSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'

  // ── «نسخ المرافق من وحدة أخرى» (tasks/public-amenities-consolidation.md D4) ──────────────────
  // Buildings here hold near-identical apartments, so ticking twenty boxes four times is the real
  // cost of this feature, not the boxes themselves. Copy-then-edit rather than building-level
  // inheritance: copied values are independent afterwards, so editing 101 never surprises 102, and
  // there are no precedence rules to explain to ops.
  const [copySources, setCopySources] = useState([]);
  const [copySourceId, setCopySourceId] = useState('');
  const [copyBusy, setCopyBusy] = useState(false);

  // The list is not fetched in edit mode, so pull it lazily the first time the dropdown is opened.
  const loadCopySources = async () => {
    if (copySources.length || copyBusy) return;
    try {
      const data = await secureApiService.getProperties({});
      setCopySources((data || []).filter((p) => p._id !== propertyId));
    } catch (err) {
      console.error('Error loading properties to copy amenities from:', err);
    }
  };

  const copyAmenitiesFrom = async () => {
    if (!copySourceId) return;
    setCopyBusy(true);
    try {
      // Fetch the full document: the list payload is not guaranteed to carry `amenities`.
      const source = await secureApiService.getProperty(copySourceId);

      // Only the paths the catalogue declares copyable — never the whole object. See
      // COPYABLE_AMENITY_PATHS for why (the source unit's WiFi password must not travel).
      let next = formData.amenities || {};
      COPYABLE_AMENITY_PATHS.forEach((path) => {
        const value = readAmenity(source?.amenities, path);
        // A type is a string that may legitimately be unset; everything else is a boolean, and an
        // absent source value means "no", not `undefined` — writing undefined would leave the
        // checkbox reading this unit's old answer while looking copied.
        next = writeAmenity(next, path, path.endsWith('.type') ? (value || '') : !!value);
      });

      // Nothing is saved here. The form is populated so ops reviews and edits before saving.
      setFormData({ ...formData, amenities: next });
      setCopySourceId('');
    } catch (err) {
      console.error('Error copying amenities:', err);
      alert('تعذّر نسخ المرافق من الوحدة المختارة');
    } finally {
      setCopyBusy(false);
    }
  };

  // Ref to hold debounce timer for guidebook auto-save
  const guidebookSaveTimer = useRef(null);

  // The check-in hour AS PERSISTED, captured on load. Deliberately a ref and not form state: the
  // form's copy is editable on another tab and only saves on submit, but the guidebook auto-saves
  // instantly, so the early check-in rule must be judged against what the server actually holds.
  const persistedCheckInTime = useRef(null);

  // Track original guidebook state for proper cancel behavior
  const originalGuidebook = useRef(null);

  const guidebookSections = formData.guidebook?.sections || createDefaultGuidebook().sections;

  // Early check-in, derived for the arrival card. `null` means the apartment has never been
  // set and inherits the portfolio default, which is UNKNOWN — so UNKNOWN is what ops see.
  // UNKNOWN is shown as «غير محدد» and NEVER as a refusal: an unfilled field is not a policy
  // of no (D154), and wording it as one would put that decision back on the floor as a
  // display bug.
  //
  // Whitelisted rather than `|| 'UNKNOWN'`: that only coerces falsy, so a hand-edited or
  // pre-migration value outside the four would leave the <select> with no matching <option> —
  // a blank control and no help line, with no clue why. Same guard the backend resolver applies.
  const storedEarlyCheckInPolicy = guidebookSections.arrival?.earlyCheckInPolicy;
  const earlyCheckInPolicy = EARLY_CHECKIN_VALUES.includes(storedEarlyCheckInPolicy)
    ? storedEarlyCheckInPolicy
    : 'UNKNOWN';

  // The PERSISTED check-in hour, not the one in the form. `bookingPageSettings.checkInTime` is
  // edited on another tab of this same form and only saves on submit, while the guidebook
  // auto-saves instantly — so validating against the unsaved value would let the client pass
  // something the route then rejects, which is exactly the confusing failure to avoid.
  const propertyCheckInTime = persistedCheckInTime.current || '15:00';

  // Mirrors the route's rule and its wording. The route is what makes it true; this is what
  // makes it kind — and both sentences now come from one file.
  const earlyCheckInRejectionReason =
    earlyCheckInPolicy === 'NOT_BEFORE'
      ? earlyCheckInRejection(guidebookSections.arrival?.earlyCheckInNotBefore, propertyCheckInTime)
      : null;
  const earlyCheckInTimeError =
    earlyCheckInRejectionReason === 'NOT_EARLIER'
      ? EARLY_CHECKIN_ERRORS.NOT_EARLIER(formatTime(propertyCheckInTime))
      : earlyCheckInRejectionReason
        ? EARLY_CHECKIN_ERRORS[earlyCheckInRejectionReason]
        : null;

  // Auto-save guidebook function with debounce
  const autoSaveGuidebook = (guidebookPayload, { isEarlyCheckInEdit = false } = {}) => {
    // Only auto-save in edit mode with a valid propertyId
    if (!propertyId || mode !== 'edit') {
      return;
    }

    // Clear existing timer
    if (guidebookSaveTimer.current) {
      clearTimeout(guidebookSaveTimer.current);
    }

    // Hold back an INCOMPLETE early check-in pair. Choosing «ليس قبل وقت محدد» leaves the hour empty
    // for however long it takes to type one, and saving through that moment would flash a failure at
    // an operator who has done nothing wrong.
    //
    // ⚠️ Two things this must NOT do, both found in review:
    //  1. It must not block the OTHER writers. Every caller builds its payload from the whole
    //     guidebook, so a blanket check here silently dropped the publish toggle and the onboarding
    //     video too — and `getChangedFields` never includes `guidebook`, so there is no second save
    //     path to recover them. Hence `isEarlyCheckInEdit`: only the edit that CAUSED the invalid
    //     state is held back.
    //  2. It must not leave the badge reading «تم الحفظ». Returning before the status update left a
    //     stale success on screen beside an unsaved change, which is the one failure worse than an
    //     error message. The timer is cleared above, and the status is set below.
    const pendingArrival = guidebookPayload?.sections?.arrival || {};
    const pairIncomplete =
      pendingArrival.earlyCheckInPolicy === 'NOT_BEFORE' &&
      earlyCheckInRejection(pendingArrival.earlyCheckInNotBefore, persistedCheckInTime.current || '15:00');
    if (isEarlyCheckInEdit && pairIncomplete) {
      setGuidebookSaveStatus('unsaved');
      return;
    }

    // Set new timer for 1.5 second debounce
    guidebookSaveTimer.current = setTimeout(async () => {
      try {
        setGuidebookSaveStatus('saving');
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000'}/api/v1/properties/${propertyId}/guidebook`,
          {
            sections: guidebookPayload.sections,
            isPublished: guidebookPayload.isPublished
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        setGuidebookSaveStatus('saved');
        setTimeout(() => setGuidebookSaveStatus('idle'), 2000);
        // A unit that predates guide links gets its token on this first save — show it now
        // rather than after a reload.
        if (!arrivalGuideLinkRef.current) refreshArrivalGuideLink();
      } catch (error) {
        console.error('Error auto-saving guidebook:', error);
        setGuidebookSaveStatus('error');
        setTimeout(() => setGuidebookSaveStatus('idle'), 3000);
      }
    }, 1500);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (guidebookSaveTimer.current) {
        clearTimeout(guidebookSaveTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        // Build params object for API call
        const params = {};
        if (selectedAccount) {
          params.ownerId = selectedAccount;
        }

        // Add status filter parameters (admin/ops only)
        if (user?.role === 'admin' || user?.role === 'ops') {
          if (statusFilter === 'all') {
            params.includeDeleted = 'true';
          } else if (statusFilter === 'deleted') {
            params.onlyDeleted = 'true';
          }
          // statusFilter === 'active' means no extra params (default behavior)
        }

        const data = await secureApiService.getProperties(params);
        setProperties(data);
        setLoading(false);
        // Statuses load after the cards so a slow status read never holds the list back; a failure
        // leaves the chips empty rather than wrong.
        unitStatusApi.getMany((data || []).filter((p) => !p.deletedAt).map((p) => p._id))
          .then((res) => setUnitStatuses(res.unitStatuses || {}))
          .catch((statusErr) => console.error('Error fetching unit statuses:', statusErr));
      } catch (err) {
        console.error('Error fetching properties:', err);
        if (err.response?.status === 403) {
          setError('ليس لديك صلاحية لعرض هذه العقارات');
        } else {
          setError('حدث خطأ أثناء تحميل العقارات');
        }
        setLoading(false);
      }
    };

    if (mode === 'list') {
      fetchProperties();
    } else if (mode === 'add') {
      // For add mode, we don't need to load anything
      setLoading(false);
    }
  }, [mode, selectedAccount, statusFilter, user?.role]); // Refetch when account or filter changes

  // Fetch owner users for dropdown
  useEffect(() => {
    const fetchOwnerUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/v1/users?role=turnKeyOwner`,
          { headers: { 'x-auth-token': token } }
        );
        // Filter for active or invited users only
        const validOwners = response.data.users.filter(u =>
          ['active', 'invited'].includes(u.status)
        );
        setOwnerUsers(validOwners);
      } catch (err) {
        console.error('Error fetching owner users:', err);
      }
    };

    // Guidebook-only users never see the owner picker, and the users list is not theirs to read.
    if ((mode === 'add' || mode === 'edit') && !isGuidebookOnly) {
      fetchOwnerUsers();
    }
  }, [mode, isGuidebookOnly]);

  // Fetch property data for editing
  useEffect(() => {
    if (mode === 'edit' && propertyId) {
      const fetchPropertyForEdit = async () => {
        try {
          const property = await secureApiService.getProperty(propertyId);

          // The platformAccounts comes as a plain object from the API, not a Map
          const platformAccountsData = property.platformAccounts || {};

          // Extract primary owner from owners array
          const primaryOwner = property.owners?.find(o => o.isPrimary);

          setFormData({
            propertyCode: property.propertyCode || '',
            name: property.name || '',
            address: property.address || '',
            city: property.city || 'جدة',
            country: property.country || 'SA',
            email: property.email || '',
            phone: property.phone || '',
            type: property.type || 'شقة',
            status: property.status || 'active',
            description: property.description || '',
            buildingName: property.buildingName || '',
            floor: property.floor || '',
            unitNumber: property.unitNumber || '',
            mapsUrl: property.mapsUrl || '',
            additionalCheckinNotesAr: property.additionalCheckinNotesAr || '',
            checkinAutomationEnabled: property.checkinAutomationEnabled || false,
            checkoutAutomationEnabled: property.checkoutAutomationEnabled || false,
            stayCheckAutomationEnabled: property.stayCheckAutomationEnabled || false,
            guestReviewAutomationEnabled: property.guestReviewAutomationEnabled || false,
            guestDisplayEnabled: property.guestDisplayEnabled || false,
            checkInAssets: property.checkInAssets || [],
            primaryOwnerId: primaryOwner?.userId?._id || primaryOwner?.userId || '',
            publicLinks: {
              airbnb: property.publicLinks?.airbnb || '',
              booking: property.publicLinks?.booking || '',
              gathern: property.publicLinks?.gathern || ''
            },
            publicListing: {
              isPublished: property.publicListing?.isPublished || false,
              sortOrder: property.publicListing?.sortOrder ?? 0
            },
            specs: {
              maxGuests: property.specs?.maxGuests ?? '',
              bedrooms: property.specs?.bedrooms ?? '',
              bathrooms: property.specs?.bathrooms ?? ''
            },
            features: property.features || {
              directBookingEnabled: false,
              onlinePaymentEnabled: false,
              promoCodesEnabled: false
            },
            platformAccounts: {
              airbnb: platformAccountsData.airbnb || {
                propertyId: '',
                listingName: '',
                calendarUrl: '',
                status: 'inactive',
                lastSync: null
              },
              booking: platformAccountsData.booking || {
                propertyId: '',
                listingName: '',
                calendarUrl: '',
                status: 'inactive',
                lastSync: null
              }
            },
            gathernMapping: property.gathernMapping || {
              propertyId: '',
              calendarUrl: '',
              exportUrl: '',
              listingName: '',
              status: 'pending',
              lastSync: null,
              lastSyncError: '',
              syncMetadata: {
                lastImportedBookings: 0,
                lastExportGenerated: null,
                importErrorCount: 0
              },
              connectedAt: null
            },
            channexMapping: property.channexMapping || {
              propertyId: '',
              roomTypes: [],
              status: 'unmapped',
              mappedAt: null
            },
            otaListings: property.otaListings || {
              booking: {
                active: false,
                propertyId: '',
                authorizationStatus: 'not_requested',
                authRequestedAt: null,
                authCompletedAt: null,
                connectedAt: null,
                lastVerified: null
              },
              airbnb: {
                active: false,
                listingId: '',
                listingUrl: '',
                authorizationStatus: 'not_requested',
                authRequestedAt: null,
                authCompletedAt: null,
                connectedAt: null,
                lastVerified: null
              }
            },
            amenities: {
              wifi: {
                available: false,
                networkName: '',
                password: '',
                instructions: '',
                instructionsAr: ''
              },
              parking: {
                available: false,
                type: 'none',
                instructions: '',
                instructionsAr: ''
              },
              pool: {
                available: false,
                hours: '',
                location: '',
                locationAr: ''
              },
              gym: {
                available: false,
                hours: '',
                location: '',
                locationAr: ''
              },
              elevator: false,
              airConditioning: false,
              heating: false,
              washingMachine: false,
              dryer: false,
              kitchen: {
                available: false,
                type: 'none'
              },
              tv: false,
              workspace: false,
              ...property.amenities
            },
            bookingPageSettings: {
              primaryPhoto: '',
              gallery: [],
              publicTitle: '',
              description: '',
              descriptionEn: '',
              checkInTime: '15:00',
              checkOutTime: '11:00',
              houseRules: [],
              houseRulesEn: [],
              cancellationPolicy: {
                type: 'moderate',
                fullRefundDays: 14,
                partialRefundDays: 7,
                partialRefundPercent: 50
              },
              importantInformation: '',
              importantInformationEn: '',
              location: {
                coordinates: {
                  lat: 0,
                  lng: 0
                },
                neighborhood: ''
              },
              ...property.bookingPageSettings
            },
            guidebook: normalizeGuidebook(property.guidebook)
          });

          // Store original guidebook for cancel restoration
          originalGuidebook.current = JSON.parse(JSON.stringify(normalizeGuidebook(property.guidebook)));
          persistedCheckInTime.current = property.bookingPageSettings?.checkInTime || null;

          // Load rate settings from property
          if (property.rateSettings) {
            setBaseRate(property.rateSettings.baseRate || 220);
            setWeekendIncrease(property.rateSettings.weekendIncrease || 30);
          }

          // Store complete original property data for change detection
          setOriginalProperty({
            name: property.name || '',
            address: property.address || '',
            city: property.city || '',
            country: property.country || '',
            email: property.email || '',
            phone: property.phone || '',
            type: property.type || '',
            status: property.status || '',
            description: property.description || '',
            buildingName: property.buildingName || '',
            floor: property.floor || '',
            unitNumber: property.unitNumber || '',
            mapsUrl: property.mapsUrl || '',
            additionalCheckinNotesAr: property.additionalCheckinNotesAr || '',
            checkinAutomationEnabled: property.checkinAutomationEnabled || false,
            checkoutAutomationEnabled: property.checkoutAutomationEnabled || false,
            stayCheckAutomationEnabled: property.stayCheckAutomationEnabled || false,
            guestReviewAutomationEnabled: property.guestReviewAutomationEnabled || false,
            guestDisplayEnabled: property.guestDisplayEnabled || false,
            checkInAssets: property.checkInAssets || [],
            primaryOwnerId: primaryOwner?.userId?._id || primaryOwner?.userId || '',
            publicLinks: property.publicLinks || {},
            publicListing: property.publicListing || {},
            specs: property.specs || {},
            features: property.features || {},
            platformAccounts: property.platformAccounts || {},
            gathernMapping: property.gathernMapping || {},
            bookingPageSettings: property.bookingPageSettings || {},
            amenities: property.amenities || {},
            guidebook: normalizeGuidebook(property.guidebook),
            rateSettings: property.rateSettings || { baseRate: 220, weekendIncrease: 30 },
            channexMapping: property.channexMapping || {} // Re-added for linkedRoomTypeId change detection
            // iCalUrls removed - deprecated field
          });
          setLoading(false);
        } catch (err) {
          console.error('Error fetching property for edit:', err);
          if (err.response?.status === 403) {
            setError('ليس لديك صلاحية لتعديل هذا العقار');
          } else {
            setError('حدث خطأ أثناء تحميل بيانات العقار');
          }
          setLoading(false);
        }
      };

      fetchPropertyForEdit();
    }
  }, [mode, propertyId]);

  // Fetch available room types for multi-unit properties
  useEffect(() => {
    const fetchRoomTypes = async () => {
      // Only fetch if property is mapped to Channex
      if (!formData.channexMapping?.propertyId) {
        return;
      }

      try {
        setLoadingRoomTypes(true);
        const response = await secureApiService.getChannexRoomTypes(formData.channexMapping.propertyId);

        if (response.success && response.data?.data) {
          setAvailableRoomTypes(response.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch room types:', error);
        // Silent fail - user can still use property without multi-unit
      } finally {
        setLoadingRoomTypes(false);
      }
    };

    fetchRoomTypes();
  }, [formData.channexMapping?.propertyId]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle image upload
  const handleImageUpload = async (file, imageType, galleryIndex = null) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('يُسمح فقط بملفات الصور (JPEG, PNG, GIF, WebP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('حجم الملف يجب أن يكون أقل من 5 ميجابايت');
      return;
    }

    setUploadingImage(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);
      formDataUpload.append('propertyId', propertyId || 'temp'); // Use 'temp' for new properties
      formDataUpload.append('imageType', imageType);
      formDataUpload.append('visibility', 'public'); // Property images are public for booking pages

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/upload/property-image`,
        formDataUpload,
        {
          headers: {
            'x-auth-token': token,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        const imageUrl = response.data.imageUrl;

        // Prepare updated bookingPageSettings
        let updatedBookingPageSettings;
        if (imageType === 'primary') {
          updatedBookingPageSettings = {
            ...formData.bookingPageSettings,
            primaryPhoto: imageUrl
          };
        } else if (imageType === 'gallery' && galleryIndex !== null) {
          const gallery = [...(formData.bookingPageSettings?.gallery || [])];
          gallery[galleryIndex] = imageUrl;
          updatedBookingPageSettings = {
            ...formData.bookingPageSettings,
            gallery
          };
        }

        // Update form data (UI state)
        setFormData(prev => ({
          ...prev,
          bookingPageSettings: updatedBookingPageSettings
        }));

        // Persist immediately to MongoDB (only in edit mode with valid propertyId)
        if (propertyId && mode === 'edit') {
          const requestId = `img-upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          try {
            // Send the whole nested object — the PUT handler only reads a nested
            // `bookingPageSettings`, so dotted paths are silently dropped (200 with no write).
            await secureApiService.updateProperty(propertyId, {
              bookingPageSettings: updatedBookingPageSettings
            });

            // Update originalProperty to reflect saved state (for change detection)
            setOriginalProperty(prev => ({
              ...prev,
              bookingPageSettings: updatedBookingPageSettings
            }));

            alert('✅ تم رفع الصورة وحفظها بنجاح');
          } catch (saveError) {
            console.error(`[${requestId}] ❌ Error saving image:`, saveError);
            alert('⚠️ تم رفع الصورة ولكن فشل الحفظ. يرجى المحاولة مرة أخرى.');
          }
        } else {
          // New property (add mode) - will save on form submit
          alert('✅ تم رفع الصورة بنجاح');
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('❌ فشل رفع الصورة: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploadingImage(false);
    }
  };

  // Check-in photos (ordered: 0 = building exterior, 1 = entrance) — mirrors the
  // bookingPageSettings gallery pattern: upload → append → immediate field-level persist.
  // Edit-mode only (widget hidden in add mode). Delivery via Channex is on hold (task §8.5).
  const handleCheckinAssetUpload = async (file) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('يُسمح فقط بملفات الصور (JPEG, PNG, GIF, WebP)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('حجم الملف يجب أن يكون أقل من 5 ميجابايت');
      return;
    }

    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);
      formDataUpload.append('propertyId', propertyId);
      formDataUpload.append('imageType', 'checkin-asset');
      formDataUpload.append('visibility', 'public');

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/upload/property-image`,
        formDataUpload,
        { headers: { 'x-auth-token': token, 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        const updatedAssets = [...(formData.checkInAssets || []), response.data.imageUrl];
        setFormData(prev => ({ ...prev, checkInAssets: updatedAssets }));

        await secureApiService.updateProperty(propertyId, { checkInAssets: updatedAssets });
        setOriginalProperty(prev => ({ ...prev, checkInAssets: updatedAssets }));
        alert('✅ تم رفع الصورة وحفظها بنجاح');
      }
    } catch (error) {
      console.error('Check-in asset upload error:', error);
      alert('❌ فشل رفع الصورة: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCheckinAssetRemove = async (index) => {
    const assets = [...(formData.checkInAssets || [])];
    const imageUrlToDelete = assets[index];
    assets.splice(index, 1);

    setFormData(prev => ({ ...prev, checkInAssets: assets }));

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/upload/property-image`,
        {
          data: { imageUrl: imageUrlToDelete, propertyId },
          headers: { 'x-auth-token': token, 'Content-Type': 'application/json' }
        }
      );
      await secureApiService.updateProperty(propertyId, { checkInAssets: assets });
      setOriginalProperty(prev => ({ ...prev, checkInAssets: assets }));
      alert('✅ تم حذف الصورة بنجاح');
    } catch (error) {
      console.error('Check-in asset delete error:', error);
      alert('❌ فشل حذف الصورة. يرجى المحاولة مرة أخرى.');
    }
  };

  // Removed: handleVideoUpload() - YouTube-only approach eliminates need for file uploads

  // `value` may be a function `(currentValue) => nextValue`, resolved against the state React is
  // actually holding rather than the render that produced the handler. Added for the arrival-guide
  // reorder arrows, where two clicks landing in one render would otherwise compute the second move
  // from a list that no longer exists. Plain values behave exactly as before.
  const handleGuidebookSectionChange = (sectionKey, fieldKey, value) => {
    setFormData((prev) => {
      const resolvedValue = typeof value === 'function'
        ? value(prev.guidebook?.sections?.[sectionKey]?.[fieldKey])
        : value;
      const updatedFields = { [fieldKey]: resolvedValue };
      // Switching arrival entry to smart-lock → clear any stored static entry code.
      // The real code is per-booking (Booking.accessCode); a stale static code here would
      // otherwise leak via the guidebook / AI assistant / PDF. Deliberate-transition ONLY
      // (not a blanket save-time normalize) so a legacy keybox code that merely defaults to
      // entryType 'smartlock' is never wiped on an unrelated save.
      if (sectionKey === 'arrival' && fieldKey === 'entryType' && resolvedValue === 'smartlock') {
        updatedFields.entryCode = '';
      }
      // Same shape, same reasoning, for early check-in: leaving NOT_BEFORE clears the hour.
      // A time left sitting beside «عند جاهزية الشقة» comes back to life the day someone
      // switches back, carrying an hour nobody re-approved. Deliberate-transition ONLY.
      if (sectionKey === 'arrival' && fieldKey === 'earlyCheckInPolicy' && resolvedValue !== 'NOT_BEFORE') {
        updatedFields.earlyCheckInNotBefore = '';
      }
      const updatedGuidebook = {
        ...prev.guidebook,
        sections: {
          ...prev.guidebook.sections,
          [sectionKey]: {
            ...prev.guidebook.sections[sectionKey],
            ...updatedFields
          }
        }
      };
      autoSaveGuidebook(updatedGuidebook, {
        isEarlyCheckInEdit:
          sectionKey === 'arrival' &&
          (fieldKey === 'earlyCheckInPolicy' || fieldKey === 'earlyCheckInNotBefore')
      });
      return {
        ...prev,
        guidebook: updatedGuidebook
      };
    });
  };

  // ====== Arrival guide — the ordered photo journey (tasks/unit-arrival-guide-page.md) ======
  //
  // Persisted through the SAME guidebook auto-save every other card on this tab uses
  // (`handleGuidebookSectionChange` → 1.5 s debounced PUT). Deliberately NOT the check-in photo
  // widget's immediate `updateProperty` call: that widget writes a ROOT field on the property and
  // this is a guidebook section, so reusing its save path would mean a second writer for the same
  // document. The debounce also batches repeated arrow clicks into one write, and the save-status
  // badge already in this tab's header reports it.
  //
  // ⚠️ Every handler rebuilds the array immutably. `normalizeGuidebook` shallow-merges, so on a
  // property with no stored guide this array is the module template's own — a push() would poison
  // the template for every property edited afterwards in this browser session.
  const ARRIVAL_GUIDE_MAX = 15;
  const arrivalGuideImages = guidebookSections.arrivalGuide?.images || [];

  const handleArrivalGuideUpload = async (file) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('يُسمح فقط بملفات الصور (JPEG, PNG, GIF, WebP)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('حجم الملف يجب أن يكون أقل من 5 ميجابايت');
      return;
    }
    if (arrivalGuideImages.length >= ARRIVAL_GUIDE_MAX) {
      alert(`الحد الأقصى ${ARRIVAL_GUIDE_MAX} صورة لدليل الوصول`);
      return;
    }

    setUploadingArrivalGuide(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);
      formDataUpload.append('propertyId', propertyId);
      // No OTA JPEG sibling is generated for this type — these images are read on a web page,
      // never pushed through the Channex attachment transport.
      formDataUpload.append('imageType', 'arrival-guide');
      formDataUpload.append('visibility', 'public');

      // 🔴 The Content-Type override is REQUIRED, not decorative. `secureApi` is created with a
      // default `Content-Type: application/json`, and axios 1.x serialises a FormData payload to
      // JSON whenever the content type says JSON — the request then reaches multer with no file
      // and fails with «لم يتم رفع أي ملف», every time. Naming `multipart/form-data` here keeps the
      // payload intact; the browser replaces this header with its own boundary-bearing version.
      // (This is why the four other upload calls in this file use raw axios — that is load-bearing,
      // not merely legacy. Using the shared instance keeps the auth + 401 interceptors.)
      const response = await secureApi.post('/api/v1/upload/property-image', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        handleGuidebookSectionChange('arrivalGuide', 'images', [
          ...arrivalGuideImages,
          response.data.imageUrl
        ]);
        // The token is minted by the save this change schedules, so the link can only be read once
        // that save has landed — hence the wait, matching the guidebook auto-save debounce.
        setTimeout(refreshArrivalGuideLink, 2000);
      }
    } catch (error) {
      console.error('Arrival guide upload error:', error);
      alert('❌ فشل رفع الصورة: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploadingArrivalGuide(false);
    }
  };

  const handleArrivalGuideRemove = async (index) => {
    const imageUrlToDelete = arrivalGuideImages[index];
    if (!imageUrlToDelete) return;
    if (!window.confirm('حذف هذه الصورة من دليل الوصول؟')) return;

    // Local-first (ARCHITECTURE_PATTERNS.md): drop it from the list — which schedules the
    // guidebook save — then clear the S3 object. A failed S3 delete leaves an orphaned file,
    // which costs pennies; the reverse order would leave a URL the page cannot render.
    handleGuidebookSectionChange('arrivalGuide', 'images',
      (current = []) => current.filter((url) => url !== imageUrlToDelete));

    try {
      await secureApi.delete('/api/v1/upload/property-image', {
        data: { imageUrl: imageUrlToDelete, propertyId }
      });
    } catch (error) {
      console.error('Arrival guide delete error:', error);
    }
  };

  // Up/down rather than drag-and-drop: ordering IS the feature (the images are a numbered
  // journey), but nine items reordered a handful of times a year does not earn a DnD library.
  const handleArrivalGuideMove = (index, direction) => {
    handleGuidebookSectionChange('arrivalGuide', 'images', (current = []) => {
      const target = index + direction;
      if (target < 0 || target >= current.length) return current;
      const reordered = [...current];
      [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
      return reordered;
    });
  };

  // The link is read after every image change rather than derived in the browser: the token is
  // minted server-side on the save that first gives a unit a guide, so the client cannot know it
  // until that save has happened. Deliberately silent on failure — a missing link is a disabled
  // row in the card, never an error dialog over an upload that worked.
  const refreshArrivalGuideLink = useCallback(async () => {
    if (mode !== 'edit' || !propertyId) return;
    try {
      const res = await secureApi.get(`/api/v1/properties/${propertyId}/arrival-guide-link`);
      setArrivalGuideLink(res.data?.url || null);
    } catch (error) {
      console.error('Arrival guide link read failed:', error);
      setArrivalGuideLink(null);
    }
  }, [mode, propertyId]);

  // The confirmation (with its explicit "I will reprint the sheet" tick) lives in
  // GuestGuideLinkCard; this only rotates the token. Throws so the card can report a failure.
  const handleArrivalGuideRegenerate = async () => {
    const res = await secureApi.post(`/api/v1/properties/${propertyId}/arrival-guide-link/regenerate`);
    setArrivalGuideLink(res.data?.url || null);
  };

  // Read once on open so a unit whose guide already exists shows its link without an edit.
  useEffect(() => { refreshArrivalGuideLink(); }, [refreshArrivalGuideLink]);

  const handleGuidebookPublishToggle = (isPublished) => {
    setFormData((prev) => {
      const updatedGuidebook = {
        ...prev.guidebook,
        isPublished
      };
      autoSaveGuidebook(updatedGuidebook);
      return {
        ...prev,
        guidebook: updatedGuidebook
      };
    });
  };

  // Removed: handleCopyVideoLink() - users can copy directly from input field

  // Change detection function - only send fields that have actually changed
  const getChangedFields = (formData, originalProperty) => {
    const changedFields = {};

    // Compare each field
    if (formData.name !== originalProperty.name) {
      changedFields.name = formData.name;
    }
    if (formData.address !== originalProperty.address) {
      changedFields.address = formData.address;
    }
    if (formData.city !== originalProperty.city) {
      changedFields.city = formData.city;
    }
    if (formData.country !== originalProperty.country) {
      changedFields.country = formData.country;
    }
    if (formData.email !== originalProperty.email) {
      changedFields.email = formData.email;
    }
    if (formData.phone !== originalProperty.phone) {
      changedFields.phone = formData.phone;
    }
    if (formData.type !== originalProperty.type) {
      changedFields.type = formData.type;
    }
    if (formData.status !== originalProperty.status) {
      changedFields.status = formData.status;
    }
    if (formData.description !== originalProperty.description) {
      changedFields.description = formData.description;
    }

    // Check-in message location fields (§0.3–0.4)
    if (formData.buildingName !== originalProperty.buildingName) {
      changedFields.buildingName = formData.buildingName;
    }
    if (formData.floor !== originalProperty.floor) {
      changedFields.floor = formData.floor;
    }
    if (formData.unitNumber !== originalProperty.unitNumber) {
      changedFields.unitNumber = formData.unitNumber;
    }
    if (formData.mapsUrl !== originalProperty.mapsUrl) {
      changedFields.mapsUrl = formData.mapsUrl;
    }
    if (formData.additionalCheckinNotesAr !== originalProperty.additionalCheckinNotesAr) {
      changedFields.additionalCheckinNotesAr = formData.additionalCheckinNotesAr;
    }
    if (formData.checkinAutomationEnabled !== originalProperty.checkinAutomationEnabled) {
      changedFields.checkinAutomationEnabled = formData.checkinAutomationEnabled;
    }
    if (formData.checkoutAutomationEnabled !== originalProperty.checkoutAutomationEnabled) {
      changedFields.checkoutAutomationEnabled = formData.checkoutAutomationEnabled;
    }
    if (formData.stayCheckAutomationEnabled !== originalProperty.stayCheckAutomationEnabled) {
      changedFields.stayCheckAutomationEnabled = formData.stayCheckAutomationEnabled;
    }
    if (formData.guestReviewAutomationEnabled !== originalProperty.guestReviewAutomationEnabled) {
      changedFields.guestReviewAutomationEnabled = formData.guestReviewAutomationEnabled;
    }
    if (formData.guestDisplayEnabled !== originalProperty.guestDisplayEnabled) {
      changedFields.guestDisplayEnabled = formData.guestDisplayEnabled;
    }

    // Public booking links — normalize both sides before comparing, because the form always holds
    // all three keys as strings while a property saved before this feature holds none at all.
    // Without this, every save would resend them.
    const normalizeLinks = (links = {}) => ({
      airbnb: (links.airbnb || '').trim(),
      booking: (links.booking || '').trim(),
      gathern: (links.gathern || '').trim()
    });
    const normalizedLinks = normalizeLinks(formData.publicLinks);
    if (JSON.stringify(normalizeLinks(originalProperty.publicLinks)) !== JSON.stringify(normalizedLinks)) {
      changedFields.publicLinks = normalizedLinks;
    }

    // Public catalogue placement — normalize both sides before comparing, for the same reason the
    // links above are: the form always holds both keys while a property saved before this feature
    // holds neither, so an unnormalized compare would resend them on every save.
    // `sortOrder` is held as a raw string while the operator types, so both sides are coerced
    // before comparing — otherwise "10" and 10 read as a change and resend on every save.
    const normalizeListing = (listing = {}) => ({
      isPublished: listing.isPublished === true,
      sortOrder: Number(String(listing.sortOrder ?? '').trim()) || 0
    });
    const normalizedListing = normalizeListing(formData.publicListing);
    if (JSON.stringify(normalizeListing(originalProperty.publicListing)) !== JSON.stringify(normalizedListing)) {
      changedFields.publicListing = normalizedListing;
    }

    // Unit specs — normalized both sides, same reason as the links and the listing above: the form
    // holds all four keys as strings while a property saved before this feature holds none.
    // An empty string is a CLEARED spec, distinct from 0, and is sent as '' so the backend unsets it.
    const normalizeSpecs = (specs = {}) => ({
      maxGuests: specs.maxGuests === '' || specs.maxGuests === undefined || specs.maxGuests === null ? '' : Number(specs.maxGuests),
      bedrooms: specs.bedrooms === '' || specs.bedrooms === undefined || specs.bedrooms === null ? '' : Number(specs.bedrooms),
      bathrooms: specs.bathrooms === '' || specs.bathrooms === undefined || specs.bathrooms === null ? '' : Number(specs.bathrooms)
    });
    const normalizedSpecs = normalizeSpecs(formData.specs);
    if (JSON.stringify(normalizeSpecs(originalProperty.specs)) !== JSON.stringify(normalizedSpecs)) {
      changedFields.specs = normalizedSpecs;
    }

    // Handle platform accounts - compare as objects
    const originalPlatforms = originalProperty.platformAccounts || {};
    const newPlatforms = formData.platformAccounts || {};
    if (JSON.stringify(originalPlatforms) !== JSON.stringify(newPlatforms)) {
      changedFields.platformAccounts = formData.platformAccounts;
    }

    // Handle Gathern mapping - compare as objects
    const originalGathern = originalProperty.gathernMapping || {};
    const newGathern = formData.gathernMapping || {};
    if (JSON.stringify(originalGathern) !== JSON.stringify(newGathern)) {
      changedFields.gathernMapping = formData.gathernMapping;
    }

    // Handle features - compare as objects
    const originalFeatures = originalProperty.features || {};
    const newFeatures = formData.features || {};
    if (JSON.stringify(originalFeatures) !== JSON.stringify(newFeatures)) {
      changedFields.features = formData.features;
    }

    // Handle bookingPageSettings - compare as objects
    const originalBookingSettings = originalProperty.bookingPageSettings || {};
    const newBookingSettings = formData.bookingPageSettings || {};
    if (JSON.stringify(originalBookingSettings) !== JSON.stringify(newBookingSettings)) {
      changedFields.bookingPageSettings = formData.bookingPageSettings;
    }

    // Handle amenities - compare as objects
    const originalAmenities = originalProperty.amenities || {};
    const newAmenities = formData.amenities || {};
    if (JSON.stringify(originalAmenities) !== JSON.stringify(newAmenities)) {
      changedFields.amenities = formData.amenities;
    }

    // Note: channexMapping is NOT sent - backend will check its own database for routing
    // Note: iCalUrls removed - deprecated field

    return changedFields;
  };

  /**
   * What the public catalogue endpoint actually requires before a unit can appear
   * (`be/routes/publicListingsRoutes.js`). Kept in the same order the endpoint applies them so the
   * two can be read side by side; the photo is the one item the endpoint tolerates and a guest does
   * not — a card with no image is the worst thing on the page, so it is listed as recommended.
   */
  const publishChecklist = [
    {
      key: 'active',
      label: 'حالة الوحدة «نشطة»',
      met: formData.status === 'active',
      required: true
    },
    {
      key: 'title',
      label: 'العنوان العام للوحدة مكتوب',
      met: !!(formData.bookingPageSettings?.publicTitle || '').trim(),
      required: true
    },
    {
      key: 'link',
      label: 'رابط حجز واحد على الأقل (Airbnb أو Booking.com أو Gathern)',
      met: PUBLIC_LINK_PLATFORMS.some(({ key }) => (formData.publicLinks?.[key] || '').trim()),
      required: true
    },
    {
      key: 'photo',
      label: 'صورة رئيسية للوحدة',
      met: !!(formData.bookingPageSettings?.primaryPhoto || '').trim(),
      required: false
    }
  ];

  // Handle property creation/update
  const handleSubmitProperty = async (e) => {
    e.preventDefault();
    // Enter in any field submits the form; a guidebook-only user must never write the property.
    if (isGuidebookOnly) return;

    // Public booking links are validated before anything is sent: the backend rejects a non-https
    // link with a 400, and losing a long edit to a single mistyped URL is the worse outcome.
    const badLink = PUBLIC_LINK_PLATFORMS.find(({ key }) => !isValidPublicLink(formData.publicLinks?.[key]));
    if (badLink) {
      setError(`رابط ${badLink.label} غير صالح — يجب أن يبدأ بـ https://`);
      return;
    }

    const badSpec = UNIT_SPECS.find(({ key, min, max }) => !isValidSpec(formData.specs?.[key], { min, max }));
    if (badSpec) {
      setError(`${badSpec.label}: يجب أن يكون رقماً صحيحاً بين ${badSpec.min} و ${badSpec.max}`);
      return;
    }

    if (!isValidSortOrder(formData.publicListing?.sortOrder)) {
      setError('ترتيب العرض يجب أن يكون رقماً صحيحاً بين 0 و 9999');
      return;
    }

    // Caught here as well as on the server so a long edit is not lost to a 400 on the one field the
    // operator cannot see from this tab. Same rule, same message as `PROPERTY_PUBLIC_TITLE_REQUIRED`.
    if (formData.publicListing?.isPublished && !(formData.bookingPageSettings?.publicTitle || '').trim()) {
      setError('أضِف «العنوان العام للوحدة» قبل عرضها في صفحة الوحدات العامة');
      return;
    }

    setCreating(true);
    setSyncStatus('idle');
    setError(null);

    try {
      if (mode === 'edit' && propertyId) {
        // Clean up gallery - remove empty strings before saving
        const cleanedFormData = {
          ...formData,
          bookingPageSettings: {
            ...formData.bookingPageSettings,
            gallery: (formData.bookingPageSettings?.gallery || []).filter(url => url && url.trim())
          }
        };

        // Use change detection to send only modified fields
        const changedFields = getChangedFields(cleanedFormData, originalProperty);

        // Check if owner has changed
        const originalOwnerId = originalProperty.primaryOwnerId || '';
        if (formData.primaryOwnerId && formData.primaryOwnerId !== originalOwnerId) {
          changedFields.owners = [{
            userId: formData.primaryOwnerId,
            isPrimary: true
          }];
        }

        // Remove primaryOwnerId from changed fields as it's not part of schema
        delete changedFields.primaryOwnerId;

        // Only include rate settings if they actually changed
        const originalBaseRate = originalProperty.rateSettings?.baseRate || 220;
        const originalWeekendIncrease = originalProperty.rateSettings?.weekendIncrease || 30;
        const currentBaseRate = Number(baseRate) || 220;
        const currentWeekendIncrease = Number(weekendIncrease) || 30;

        if (originalBaseRate !== currentBaseRate || originalWeekendIncrease !== currentWeekendIncrease) {
          changedFields.rateSettings = {
            baseRate: currentBaseRate,
            weekendIncrease: currentWeekendIncrease
          };
        }

        // Check if linkedRoomTypeId changed (multi-unit property support)
        const originalLinkedRoomTypeId = originalProperty.channexMapping?.linkedRoomTypeId || null;
        const currentLinkedRoomTypeId = formData.channexMapping?.linkedRoomTypeId || null;
        let linkedRoomTypeChanged = false;

        if (originalLinkedRoomTypeId !== currentLinkedRoomTypeId) {
          // Use dedicated endpoint for linkedRoomTypeId (it's internal channexMapping state)
          try {
            await secureApiService.updateLinkedRoomType(propertyId, currentLinkedRoomTypeId);
            linkedRoomTypeChanged = true;
          } catch (error) {
            console.error('Failed to update linked room type:', error);
            throw new Error('فشل تحديث نوع الغرفة المرتبط');
          }
        }

        // Only proceed if there are actual changes
        if (Object.keys(changedFields).length === 0 && !linkedRoomTypeChanged) {
          setSyncStatus('success');
          setSyncMessage('لا توجد تغييرات للحفظ');
        } else {
          if (Object.keys(changedFields).length > 0) {
            await secureApiService.updateProperty(propertyId, changedFields);
          }
          setSyncStatus('success');

          // Build success message based on what was updated
          let successMsg = '✅ تم تحديث العقار بنجاح';
          if (linkedRoomTypeChanged) {
            successMsg = currentLinkedRoomTypeId
              ? '✅ تم تحديث العقار وربط نوع الغرفة بنجاح'
              : '✅ تم تحديث العقار وإلغاء ربط نوع الغرفة';
          }
          setSyncMessage(successMsg);
        }
      } else {
        // Prepare property data with owners array
        const propertyData = {
          ...formData,
          rateSettings: {
            baseRate: Number(baseRate) || 220,
            weekendIncrease: Number(weekendIncrease) || 30
          }
        };
        if (formData.primaryOwnerId) {
          propertyData.owners = [{
            userId: formData.primaryOwnerId,
            isPrimary: true
          }];
        }
        // Remove primaryOwnerId as it's not part of the schema
        delete propertyData.primaryOwnerId;

        await secureApiService.createProperty(propertyData);
      }

      // Only navigate after a short delay if syncing was successful or not needed
      if (syncStatus !== 'error') {
        setTimeout(() => {
          navigate('/properties');
        }, syncStatus === 'success' ? 1500 : 0);
      } else {
        setCreating(false);
      }
    } catch (err) {
      console.error('Error saving property:', err);
      if (err.response?.status === 403) {
        setError('ليس لديك صلاحية لإجراء هذا الإجراء');
      } else if (err.response?.status === 400 && err.response?.data?.message) {
        // A rejected field (e.g. a malformed booking link) — show what the server actually refused
        // instead of the generic failure, which leaves the user with nothing to correct.
        setError(err.response.data.message);
      } else {
        setError(mode === 'edit' ? 'حدث خطأ أثناء تحديث العقار' : 'حدث خطأ أثناء إنشاء العقار');
      }
      setCreating(false);
      setSyncStatus('idle');
    }
  };

  // Handle Full Calendar Sync (Step 4)
  const handleFullCalendarSync = async () => {
    setSyncStatus('loading');
    setSyncMessage('جاري مزامنة التقويم الكامل...');

    try {
      // Generate 365 days of data starting from tomorrow
      const startDate = new Date();
      startDate.setDate(startDate.getDate() + 1);

      const fullData = [];
      const basePrice = Number(baseRate) || 220; // Use configured base rate
      const weekendMultiplier = 1 + (Number(weekendIncrease) ?? 0) / 100; // Calculate multiplier (default 0% increase)

      // Get room types with rate plans from the current property
      const roomMappings = formData.channexMapping?.roomMappings || {};

      // Generate data for each room and rate plan
      Object.entries(roomMappings).forEach(([roomId, mapping]) => {
        if (mapping.ratePlans && mapping.ratePlans.length > 0) {
          // Generate 365 days of data
          for (let i = 0; i < 365; i++) {
            const date = new Date(startDate);
            date.setDate(date.getDate() + i);
            const dateStr = date.toISOString().split('T')[0];

            const isWeekend = date.getDay() === 5 || date.getDay() === 6;

            mapping.ratePlans.forEach(ratePlan => {
              fullData.push({
                date: dateStr,
                roomTypeId: roomId,
                ratePlanId: ratePlan.id,
                inventory: {
                  total: 1, // Apartment has 1 unit
                  available: 1
                },
                rates: {
                  standard: isWeekend ? Math.round(basePrice * weekendMultiplier) : basePrice,
                  currency: 'SAR'
                },
                restrictions: {
                  minStayArrival: 1, // Same minimum stay for all days (no weekend restriction)
                  minStayThrough: 1,
                  maxStay: 30,
                  closedToArrival: false,
                  closedToDeparture: false,
                  stopSell: false
                }
              });
            });
          }
        }
      });

      if (fullData.length === 0) {
        setSyncStatus('idle');
        setSyncMessage('لا توجد غرف أو خطط أسعار للمزامنة');
        return;
      }

      // Send to backend
      const response = await axios.post('/api/v1/channex/full-sync', {
        propertyId: propertyId,
        data: fullData,
        testType: 'full_sync'
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setSyncStatus('success');
        setSyncMessage(`✅ تمت المزامنة بنجاح - ${fullData.length} سجل`);

        // Update the lastSyncedAt and initialSyncCompleted in form data
        if (response.data.lastSyncedAt) {
          setFormData(prev => ({
            ...prev,
            channexMapping: {
              ...prev.channexMapping,
              lastSyncedAt: response.data.lastSyncedAt,
              initialSyncCompleted: response.data.results?.initialSyncCompleted || true,
              initialSyncCompletedAt: response.data.lastSyncedAt
            }
          }));
        }

        // Clear message after 5 seconds
        setTimeout(() => {
          setSyncStatus('idle');
          setSyncMessage('');
        }, 5000);
      }
    } catch (error) {
      console.error('Calendar sync error:', error);
      setSyncStatus('error');
      setSyncMessage(`❌ فشلت المزامنة: ${error.response?.data?.error || error.message}`);

      // Clear error after 5 seconds
      setTimeout(() => {
        setSyncStatus('idle');
        setSyncMessage('');
      }, 5000);
    }
  };

  // Handle property deletion
  const handleDeleteProperty = async () => {
    if (!propertyId) return;

    setDeleting(true);
    try {
      await secureApiService.deleteProperty(propertyId);
      setShowDeleteModal(false);
      navigate('/properties');
    } catch (err) {
      console.error('Error deleting property:', err);
      console.error('❌ DELETE 400 Response:', err.response?.data);
      console.error('❌ DELETE 400 Status:', err.response?.status);
      console.error('❌ DELETE 400 Headers:', err.response?.headers);
      if (err.response?.status === 403) {
        setError('ليس لديك صلاحية لحذف هذا العقار');
      } else {
        setError('حدث خطأ أثناء حذف العقار');
      }
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // Handle cancel with guidebook restoration
  const handleCancel = async () => {
    // Guidebook-only users (ops, G11) have no «تحديث العقار» — leaving is their only way out, so it
    // must KEEP what auto-save stored, not roll it back. A save still inside the 1.5 s debounce is
    // sent now rather than dropped.
    if (isGuidebookOnly) {
      if (guidebookSaveTimer.current) {
        clearTimeout(guidebookSaveTimer.current);
        guidebookSaveTimer.current = null;
        try {
          await secureApi.put(`/api/v1/properties/${propertyId}/guidebook`, {
            sections: formData.guidebook?.sections,
            isPublished: formData.guidebook?.isPublished
          });
        } catch (error) {
          console.error('Error saving guidebook on leave:', error);
        }
      }
      navigate('/properties');
      return;
    }

    // Clear pending auto-save timer
    if (guidebookSaveTimer.current) {
      clearTimeout(guidebookSaveTimer.current);
      guidebookSaveTimer.current = null;
    }

    // If in edit mode and guidebook was modified, restore original
    if (mode === 'edit' && originalGuidebook.current && propertyId) {
      const currentGuidebook = formData.guidebook;

      // 🔴 The arrival-guide image list is EXEMPT from this rollback, and deliberately so.
      // Everything else on the guidebook tab is typed text, where «إلغاء» meaning "forget what I
      // just typed" is exactly right. An image is not a keystroke: by the time it is in this list
      // the file is already in S3 and the operator has been shown «تم الحفظ». Rolling it back would
      // wipe a nine-image guide because of an unrelated edit on another tab and strand the files
      // where no screen can reach them. The mirror case is just as bad the other way: restoring a
      // URL whose S3 object was already deleted leaves a step that renders as a broken image.
      // Carry the LIVE list into the restore payload, and ignore it when deciding whether to
      // restore at all, so an images-only session does not trigger a pointless rollback.
      const restoredSections = {
        ...originalGuidebook.current.sections,
        arrivalGuide: currentGuidebook?.sections?.arrivalGuide
          || originalGuidebook.current.sections?.arrivalGuide
      };

      const comparable = (sections) => JSON.stringify({ ...sections, arrivalGuide: undefined });
      const textChanged =
        currentGuidebook?.isPublished !== originalGuidebook.current.isPublished ||
        comparable(currentGuidebook?.sections) !== comparable(originalGuidebook.current.sections);

      // Cancel clears the pending auto-save above, so an upload finished inside the 1.5 s debounce
      // has NOT reached the database yet. Without this the file would sit in S3 with nothing
      // pointing at it. One write settles both jobs: it restores the text and commits the images.
      const imagesChanged =
        JSON.stringify(currentGuidebook?.sections?.arrivalGuide?.images || []) !==
        JSON.stringify(originalGuidebook.current.sections?.arrivalGuide?.images || []);

      if (textChanged || imagesChanged) {
        try {
          await axios.put(
            `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000'}/api/v1/properties/${propertyId}/guidebook`,
            {
              sections: restoredSections,
              isPublished: originalGuidebook.current.isPublished
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            }
          );
        } catch (error) {
          console.error('Error restoring guidebook on cancel:', error);
        }
      }
    }

    // Navigate back
    navigate('/properties');
  };

  // Handle saving linked room type for multi-unit properties
  const handleSaveLinkedRoomType = async () => {
    if (!propertyId || !formData.channexMapping?.linkedRoomTypeId) {
      return;
    }

    setSavingLinkedRoomType(true);
    try {
      // STEP 1: Save channexMapping.propertyId if it's from manual input
      if (formData.channexMapping?.manualPropertyId) {
        const setPropertyIdResponse = await axios.put(
          `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000'}/api/v1/properties/${propertyId}/channex-property-id`,
          {
            channexPropertyId: formData.channexMapping.manualPropertyId
          },
          {
            headers: {
              'x-auth-token': localStorage.getItem('token')
            }
          }
        );

        if (!setPropertyIdResponse.data.success) {
          throw new Error('Failed to set Channex Property ID');
        }
      }

      // STEP 2: Now link the room type (syncs room data and rate plans)
      const response = await secureApiService.updateLinkedRoomType(
        propertyId,
        formData.channexMapping.linkedRoomTypeId
      );

      if (response.success) {
        alert('✅ تم حفظ الربط بنجاح! تم مزامنة بيانات نوع الغرفة وخطط الأسعار.');
        // Reload page to reflect updated channexMapping
        window.location.reload();
      }
    } catch (error) {
      console.error('Error saving linked room type:', error);
      alert('❌ فشل حفظ الربط: ' + (error.response?.data?.message || error.message));
    } finally {
      setSavingLinkedRoomType(false);
    }
  };

  // Function to format property status in Arabic
  const formatStatus = (property) => {
    // Check if property is deleted (soft delete)
    if (property.deletedAt) {
      return 'محذوف';
    }

    const status = property.status || 'active';
    switch (status) {
      case 'active':
        return 'نشط';
      case 'maintenance':
        return 'تحت الصيانة';
      case 'inactive':
        return 'غير نشط';
      default:
        return status;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="properties-loading" dir="rtl">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">جاري التحميل...</span>
        </div>
        <p>جاري تحميل العقارات...</p>
      </div>
    );
  }

  // Show property creation/edit form when mode is "add" or "edit"
  if (mode === 'add' || mode === 'edit') {
    // Check permissions for adding/editing
    const canAdd = user?.role === 'admin';
    const canEdit = user?.role === 'admin' || isGuidebookOnly;
    
    if (!canAdd && mode === 'add') {
      return (
        <div className="properties-management" dir="rtl">
          <div className="alert alert-danger" role="alert">
            ليس لديك صلاحية لإضافة عقارات جديدة
          </div>
          <Link to="/properties" className="btn btn-secondary">
            العودة إلى قائمة العقارات
          </Link>
        </div>
      );
    }
    
    if (!canEdit && mode === 'edit') {
      return (
        <div className="properties-management" dir="rtl">
          <div className="alert alert-danger" role="alert">
            ليس لديك صلاحية لتعديل هذا العقار
          </div>
          <Link to="/properties" className="btn btn-secondary">
            العودة إلى قائمة العقارات
          </Link>
        </div>
      );
    }
    
    return (
      <div className="properties-management" dir="rtl">
        <div className="properties-management-header">
          <h1>{mode === 'edit' ? 'تعديل العقار' : 'إضافة عقار جديد'}</h1>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleCancel}
          >
            <i className="bi bi-arrow-right"></i> العودة إلى قائمة العقارات
          </button>
        </div>
        
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <div className="property-form-container">
          {/* Tab Navigation */}
          <div className="property-tabs mb-4">
            <nav className="tabs-nav d-flex gap-2 border-bottom">
              {!isGuidebookOnly && (<>
              <button
                type="button"
                className={`tab-button ${activeTab === 'basic' ? 'active' : ''}`}
                onClick={() => setActiveTab('basic')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderBottom: activeTab === 'basic' ? '2px solid #471396' : '2px solid transparent',
                  background: 'none',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'basic' ? 'bold' : 'normal',
                  color: activeTab === 'basic' ? '#471396' : '#666'
                }}
              >
                المعلومات الأساسية
              </button>
              <button
                type="button"
                className={`tab-button ${activeTab === 'publicContent' ? 'active' : ''}`}
                onClick={() => setActiveTab('publicContent')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderBottom: activeTab === 'publicContent' ? '2px solid #471396' : '2px solid transparent',
                  background: 'none',
                  cursor: 'pointer',
                  fontWeight: activeTab === 'publicContent' ? 'bold' : 'normal',
                  color: activeTab === 'publicContent' ? '#471396' : '#666'
                }}
              >
                محتوى الصفحة العامة
              </button>
              </>)}
              {/* Guidebook Tab - Only show in edit mode */}
              {mode === 'edit' && (
                <button
                  type="button"
                  className={`tab-button ${activeTab === 'guidebook' ? 'active' : ''}`}
                  onClick={() => setActiveTab('guidebook')}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderBottom: activeTab === 'guidebook' ? '2px solid #471396' : '2px solid transparent',
                    background: 'none',
                    cursor: 'pointer',
                    fontWeight: activeTab === 'guidebook' ? 'bold' : 'normal',
                    color: activeTab === 'guidebook' ? '#471396' : '#666'
                  }}
                >
                  دليل العقار
                </button>
              )}
            </nav>
          </div>

          <form onSubmit={handleSubmitProperty} className="property-form">
            {/* Tab 1: Basic Information */}
            {activeTab === 'basic' && (
              <div className="basic-info-tab">
                <h3 className="form-section-title">المعلومات الأساسية</h3>
            
            <div className="form-group mb-3">
              <label htmlFor="name" className="form-label">اسم العقار *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="form-control"
                placeholder="أدخل اسم العقار"
              />
            </div>

            <div className="form-group mb-3">
              <label htmlFor="address" className="form-label">العنوان *</label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
                className="form-control"
                placeholder="أدخل عنوان العقار"
              />
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label htmlFor="type" className="form-label">نوع العقار *</label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    required
                    className="form-control"
                  >
                    <option value="شقة">🏠 وحدة سكنية (شقة)</option>
                    <option value="فيلا">🏡 وحدة سكنية (فيلا)</option>
                  </select>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label htmlFor="status" className="form-label">حالة العقار *</label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    required
                    className="form-control"
                  >
                    <option value="active">نشط</option>
                    <option value="maintenance">تحت الصيانة</option>
                    <option value="inactive">غير نشط</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label htmlFor="city" className="form-label">المدينة *</label>
                  <select
                    id="city"
                    name="city"
                    value={formData.city || 'جدة'}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  >
                    <option value="">اختر المدينة</option>
                    <option value="جدة">جدة</option>
                    <option value="الرياض">الرياض</option>
                    <option value="مكة المكرمة">مكّة المكرمة</option>
                    <option value="المدينة المنورة">المدينة المنورة</option>
                    <option value="الدمام">الدمام</option>
                    <option value="الطائف">الطائف</option>
                    <option value="الخبر">الخبر</option>
                    <option value="أبها">أبها</option>
                  </select>
                  <small className="text-muted">سيتم إرسالها بالإنجليزية إلى Channex</small>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label htmlFor="country" className="form-label">الدولة *</label>
                  <select
                    id="country"
                    name="country"
                    value={formData.country || 'SA'}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  >
                    <option value="SA">المملكة العربية السعودية</option>
                    <option value="AE">الإمارات العربية المتحدة</option>
                    <option value="KW">الكويت</option>
                    <option value="QA">قطر</option>
                    <option value="BH">البحرين</option>
                    <option value="OM">عمان</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label htmlFor="email" className="form-label">البريد الإلكتروني (اختياري)</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="property@example.com"
                  />
                  <small className="text-muted">مطلوب لربط القنوات</small>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label htmlFor="phone" className="form-label">رقم الهاتف (اختياري)</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="0501234567"
                    dir="ltr"
                  />
                  <small className="text-muted">سيتم تنسيقه تلقائياً (+966)</small>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-12">
                <div className="form-group mb-3">
                  <label htmlFor="primaryOwnerId" className="form-label">
                    المالك <span className="text-danger">*</span>
                  </label>

                  {mode === 'edit' && originalProperty?.primaryOwnerId && (
                    <div className="alert alert-info" role="alert" style={{fontSize: '0.9rem', padding: '0.5rem 1rem'}}>
                      <i className="bi bi-info-circle me-2"></i>
                      <strong>المالك الحالي:</strong> {ownerUsers.find(o => o._id === originalProperty.primaryOwnerId)?.name || 'غير معروف'}
                      {' '}({ownerUsers.find(o => o._id === originalProperty.primaryOwnerId)?.email || ''})
                    </div>
                  )}

                  <select
                    id="primaryOwnerId"
                    name="primaryOwnerId"
                    value={formData.primaryOwnerId}
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  >
                    <option value="">اختر المالك</option>
                    {ownerUsers.map(owner => (
                      <option key={owner._id} value={owner._id}>
                        {owner.name} ({owner.email}) - {owner.status === 'invited' ? 'مدعو' : 'نشط'}
                      </option>
                    ))}
                  </select>
                  <small className="text-muted">
                    اختر المالك الرئيسي للعقار من قائمة الملاك المسجلين
                  </small>
                </div>
              </div>
            </div>

            <div className="form-group mb-3">
              <label htmlFor="description" className="form-label">الوصف</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="form-control"
                rows="3"
                placeholder="أدخل وصف العقار (اختياري)"
              />
            </div>

            {/* Unit facts a guest compares (tasks/public-listings-page.md step 6).
                Edit-mode only, like every other block below the basics — the create route accepts
                the essentials and nothing else, so a value typed here while creating would be
                dropped with a success message. That is the house pattern, not a workaround:
                amenities, booking links and page content all behave the same way. */}
            {mode === 'edit' && propertyId && (
              <>
                <h3 className="form-section-title mt-4">مواصفات الوحدة</h3>
                <p className="text-muted" style={{ fontSize: '0.85rem', marginTop: '-0.5rem', marginBottom: '1rem' }}>
                  تظهر على بطاقة الوحدة في صفحة الوحدات العامة. اترك أي حقل فارغاً إن لم يكن معروفاً — لن يظهر على البطاقة.
                </p>

                <div className="row">
                  {UNIT_SPECS.map(({ key, label, min, max, hint }) => {
                    const value = formData.specs?.[key] ?? '';
                    const invalid = !isValidSpec(value, { min, max });
                    return (
                      <div className="col-6 col-md-4 mb-3" key={key}>
                        <label htmlFor={`spec-${key}`} className="form-label">{label}</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          id={`spec-${key}`}
                          className={`form-control${invalid ? ' is-invalid' : ''}`}
                          style={{ textAlign: 'right' }}
                          value={value}
                          onChange={(e) => setFormData({
                            ...formData,
                            specs: { ...formData.specs, [key]: e.target.value }
                          })}
                        />
                        {invalid ? (
                          <div className="invalid-feedback d-block">
                            رقم صحيح بين {min} و {max}
                          </div>
                        ) : hint ? (
                          <small className="text-muted d-block mt-1">{hint}</small>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Check-in message location fields (§0.3–0.4) — feed the automated check-in message */}
            <h3 className="form-section-title mt-4">بيانات الوصول والموقع</h3>
            <p className="text-muted" style={{ fontSize: '0.85rem', marginTop: '-0.5rem', marginBottom: '1rem' }}>
              تُستخدم هذه البيانات في رسالة تسجيل الدخول التلقائية التي تُرسل للضيف قبل موعد الوصول. اترك أي حقل فارغاً ولن يظهر في الرسالة.
            </p>

            <div className="form-group mb-3">
              <label htmlFor="buildingName" className="form-label">اسم المبنى / الموقع</label>
              <input
                type="text"
                id="buildingName"
                name="buildingName"
                value={formData.buildingName || ''}
                onChange={handleInputChange}
                className="form-control"
                placeholder="مثال: مبنى الشاشة"
                maxLength={200}
              />
              <small className="text-muted">يظهر في سطر «الموقع :» بالرسالة</small>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label htmlFor="floor" className="form-label">الدور</label>
                  <input
                    type="text"
                    id="floor"
                    name="floor"
                    value={formData.floor || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="مثال: الأول / الأرضي"
                    maxLength={50}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label htmlFor="unitNumber" className="form-label">رقم الشقة</label>
                  <input
                    type="text"
                    id="unitNumber"
                    name="unitNumber"
                    value={formData.unitNumber || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="مثال: 12"
                    maxLength={50}
                  />
                </div>
              </div>
            </div>

            <div className="form-group mb-3">
              <label htmlFor="mapsUrl" className="form-label">رابط الموقع على خرائط جوجل</label>
              <input
                type="url"
                id="mapsUrl"
                name="mapsUrl"
                value={formData.mapsUrl || ''}
                onChange={handleInputChange}
                className="form-control"
                placeholder="https://maps.app.goo.gl/..."
                dir="ltr"
                maxLength={1000}
              />
              <small className="text-muted">الصق رابط المشاركة من خرائط جوجل مباشرةً</small>
            </div>

            <div className="form-group mb-3">
              <label htmlFor="additionalCheckinNotesAr" className="form-label">ملاحظات إضافية لتسجيل الدخول</label>
              <textarea
                id="additionalCheckinNotesAr"
                name="additionalCheckinNotesAr"
                value={formData.additionalCheckinNotesAr || ''}
                onChange={handleInputChange}
                className="form-control"
                rows="3"
                placeholder="أي تعليمات إضافية تُضاف لرسالة تسجيل الدخول (اختياري)"
                maxLength={2000}
              />
            </div>

            {/* Check-in photos (ordered: 1 = واجهة المبنى, 2 = المدخل) — max 2, edit mode only (task §8.5) */}
            {mode === 'edit' && propertyId && (
              <div className="form-group mb-3">
                <label className="form-label">صور الوصول (واجهة المبنى والمدخل)</label>
                <div className="d-flex gap-3 flex-wrap">
                  {(formData.checkInAssets || []).map((photo, index) => (
                    <div key={photo} style={{ position: 'relative', width: '140px' }}>
                      <img
                        src={photo}
                        alt={index === 0 ? 'واجهة المبنى' : 'مدخل العقار'}
                        style={{ width: '140px', height: '100px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #dee2e6' }}
                      />
                      <button
                        type="button"
                        onClick={() => handleCheckinAssetRemove(index)}
                        style={{
                          position: 'absolute', top: '4px', insetInlineEnd: '4px',
                          background: 'rgba(220,53,69,0.9)', color: 'white', border: 'none',
                          padding: '2px 6px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer'
                        }}
                      >
                        ✕
                      </button>
                      <small className="text-muted d-block text-center mt-1">
                        {index === 0 ? 'واجهة المبنى' : 'مدخل العقار'}
                      </small>
                    </div>
                  ))}
                  {(formData.checkInAssets || []).length < 2 && (
                    <label
                      htmlFor="checkin-asset-upload"
                      style={{
                        width: '140px', height: '100px', border: '2px dashed #ced4da', borderRadius: '8px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: uploadingImage ? 'wait' : 'pointer', color: '#6c757d', fontSize: '0.85rem'
                      }}
                    >
                      {uploadingImage ? 'جارٍ الرفع…' : '+ إضافة صورة'}
                    </label>
                  )}
                </div>
                <input
                  type="file"
                  id="checkin-asset-upload"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  style={{ display: 'none' }}
                  disabled={uploadingImage}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleCheckinAssetUpload(file);
                    e.target.value = '';
                  }}
                />
                <small className="text-muted d-block mt-1">
                  الصورة الأولى: واجهة المبنى، الثانية: مدخل العقار. تُحفظ فور الرفع.
                </small>
              </div>
            )}

            {/* Direct Booking Features */}
            {mode === 'edit' && propertyId && (
              <>
                <h3 className="form-section-title mt-4">الميزات</h3>

                <div className="form-group mb-3">
                  <div className="form-check form-switch">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="directBookingEnabled"
                      checked={formData.features?.directBookingEnabled || false}
                      onChange={(e) => setFormData({
                        ...formData,
                        features: {
                          ...formData.features,
                          directBookingEnabled: e.target.checked
                        }
                      })}
                      style={{ cursor: 'pointer' }}
                    />
                    <label className="form-check-label" htmlFor="directBookingEnabled" style={{ cursor: 'pointer' }}>
                      <strong>تفعيل الحجز المباشر</strong>
                      <br />
                      <small className="text-muted">
                        السماح للضيوف بالحجز مباشرة عبر رابط العقار
                        {formData.features?.directBookingEnabled && (
                          <>
                            <br />
                            <span className="text-primary" style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                              🔗 الرابط: {getDirectBookingUrl(propertyId)}
                            </span>
                            <br />
                            <a
                              href={getDirectBookingUrl(propertyId)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="badge bg-primary"
                              style={{ textDecoration: 'none', marginTop: '4px', display: 'inline-block' }}
                            >
                              معاينة صفحة الحجز ↗
                            </a>
                          </>
                        )}
                      </small>
                    </label>
                  </div>
                </div>

                {/* Unit automation switches — ops-only (founder 13 Sep, post-checkout review L53). The server
                    ignores them from anyone else, so no owner is shown them either. */}
                {CORE_STAFF_ROLES.includes(user?.role) && (
                <>
                {/* Automated check-in message — per-property rollout toggle (§0.5) */}
                <div className="form-group mb-3">
                  <div className="form-check form-switch">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="checkinAutomationEnabled"
                      checked={formData.checkinAutomationEnabled || false}
                      onChange={(e) => setFormData({ ...formData, checkinAutomationEnabled: e.target.checked })}
                      style={{ cursor: 'pointer' }}
                    />
                    <label className="form-check-label" htmlFor="checkinAutomationEnabled" style={{ cursor: 'pointer' }}>
                      <strong>تفعيل رسالة تسجيل الدخول التلقائية</strong>
                      <br />
                      <small className="text-muted">
                        إرسال رسالة تسجيل الدخول تلقائياً للضيف قبل 24 ساعة من موعد الوصول. يتطلب تعبئة «بيانات الوصول والموقع» ودليل العقار (تعليمات الدخول والواي فاي).
                      </small>
                    </label>
                  </div>
                </div>

                {/* Automated checkout reminder — its OWN per-property toggle
                    (tasks/checkout-message-automation.md §0.9). Separate from the check-in
                    toggle on purpose: the two automations ramp independently, and this one
                    needs no guidebook data. */}
                <div className="form-group mb-3">
                  <div className="form-check form-switch">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="checkoutAutomationEnabled"
                      checked={formData.checkoutAutomationEnabled || false}
                      onChange={(e) => setFormData({ ...formData, checkoutAutomationEnabled: e.target.checked })}
                      style={{ cursor: 'pointer' }}
                    />
                    <label className="form-check-label" htmlFor="checkoutAutomationEnabled" style={{ cursor: 'pointer' }}>
                      <strong>تفعيل رسالة تذكير المغادرة التلقائية</strong>
                      <br />
                      <small className="text-muted">
                        إرسال تذكير بموعد المغادرة تلقائياً للضيف قبل ساعة من «وقت تسجيل المغادرة» المحدد في تبويب «محتوى الصفحة العامة» — ما لم يُمنح تمديد لهذا الحجز، فيُحتسب وقت المغادرة الجديد. لا يتطلب تعبئة دليل العقار.
                      </small>
                    </label>
                  </div>
                </div>

                {/* In-stay check message «رسالة الاطمئنان» — its OWN per-unit toggle
                    (tasks/stay-check-message.md D18), so it pilots on a few units first. */}
                <div className="form-group mb-3">
                  <div className="form-check form-switch">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="stayCheckAutomationEnabled"
                      checked={formData.stayCheckAutomationEnabled || false}
                      onChange={(e) => setFormData({ ...formData, stayCheckAutomationEnabled: e.target.checked })}
                      style={{ cursor: 'pointer' }}
                    />
                    <label className="form-check-label" htmlFor="stayCheckAutomationEnabled" style={{ cursor: 'pointer' }}>
                      <strong>تفعيل رسالة الاطمئنان التلقائية</strong>
                      <br />
                      <small className="text-muted">
                        إرسال رسالة للضيف بعد ساعتين من أول دخول له للشقة (بالرمز الخاص بحجزه) للتأكد أن كل شيء كما توقع — بين ٩:٣٠ صباحاً و١٠:٠٠ مساءً فقط. لا تُرسل إذا تواصل الضيف معنا بعد دخوله أو كان لحجزه طلب أو شكوى مفتوحة.
                      </small>
                    </label>
                  </div>
                </div>

                {/* Post-checkout guest review — per-unit switchover (tasks/post-checkout-review-and-replies.md
                    L40). Once ON, the «التقييمات» page prepares our Airbnb review of each departed guest and
                    the thank-you for ops to approve, and ops stops reviewing this unit's guests by hand in the
                    Airbnb app. Default OFF, the same ramp shape as the two automation toggles above. */}
                <div className="form-group mb-3">
                  <div className="form-check form-switch">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="guestReviewAutomationEnabled"
                      checked={formData.guestReviewAutomationEnabled || false}
                      onChange={(e) => setFormData({ ...formData, guestReviewAutomationEnabled: e.target.checked })}
                      style={{ cursor: 'pointer' }}
                    />
                    <label className="form-check-label" htmlFor="guestReviewAutomationEnabled" style={{ cursor: 'pointer' }}>
                      <strong>تفعيل تقييم الضيوف من صفحة «التقييمات والشكاوى»</strong>
                      <br />
                      <small className="text-muted">
                        بعد إغلاق تنظيف المغادرة يُجهَّز تقييمنا للضيف على Airbnb ورسالة الشكر في صفحة «التقييمات والشكاوى» لاعتمادهما قبل الإرسال. عند التفعيل يتوقف فريق العمليات عن تقييم ضيوف هذه الوحدة يدوياً في تطبيق Airbnb.
                      </small>
                    </label>
                  </div>
                </div>

                {/* In-apartment guest display — per-unit rollout toggle
                    (tasks/guest-display-unit-page.md D16). Same ramp shape as the two automation
                    toggles above and default OFF, because a unit with no box must not be able to
                    serve a screen. This is also the honest record of which apartments have one. */}
                <div className="form-group mb-3">
                  <div className="form-check form-switch">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="guestDisplayEnabled"
                      checked={formData.guestDisplayEnabled || false}
                      onChange={(e) => setFormData({ ...formData, guestDisplayEnabled: e.target.checked })}
                      style={{ cursor: 'pointer' }}
                    />
                    <label className="form-check-label" htmlFor="guestDisplayEnabled" style={{ cursor: 'pointer' }}>
                      <strong>تفعيل شاشة الترحيب داخل الوحدة</strong>
                      <br />
                      <small className="text-muted">
                        تفعيل شاشة الترحيب المعروضة على تلفاز الوحدة (اسم الضيف، الواي فاي، وقت المغادرة). فعّلها فقط للوحدات المزوّدة بجهاز عرض. يتطلب تعبئة «الواي فاي» في دليل العقار.
                      </small>
                    </label>
                  </div>
                </div>
                </>
                )}
              </>
            )}

            {/* Public booking links — the guest-facing listing URL per platform.
                tasks/property-public-booking-links.md. Entered once at onboarding by admin/owner;
                ops reads and copies them from the property page, where they can't be edited.
                Edit-mode only: a property being created is not listed anywhere yet. */}
            {mode === 'edit' && propertyId && (
              <>
                <h3 className="form-section-title mt-4">روابط الحجز العامة</h3>
                <p className="text-muted mb-3" style={{ fontSize: '0.88rem' }}>
                  رابط صفحة الوحدة على كل منصة كما يراه الضيف. تظهر هذه الروابط لفريق العمليات في صفحة العقار لنسخها ومشاركتها.
                </p>

                {PUBLIC_LINK_PLATFORMS.map(({ key, label, placeholder }) => {
                  const value = formData.publicLinks?.[key] || '';
                  const invalid = !isValidPublicLink(value);
                  return (
                    <div className="form-group mb-3" key={key}>
                      <label htmlFor={`public-link-${key}`} className="form-label d-flex align-items-center" style={{ gap: '8px' }}>
                        <span
                          aria-hidden="true"
                          style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            backgroundColor: getPlatformColor(key),
                            display: 'inline-block',
                            flexShrink: 0
                          }}
                        />
                        {label}
                      </label>
                      <input
                        type="url"
                        id={`public-link-${key}`}
                        className={`form-control${invalid ? ' is-invalid' : ''}`}
                        dir="ltr"
                        style={{ textAlign: 'left' }}
                        placeholder={placeholder}
                        maxLength={1000}
                        value={value}
                        onChange={(e) => setFormData({
                          ...formData,
                          publicLinks: { ...formData.publicLinks, [key]: e.target.value }
                        })}
                      />
                      {invalid && (
                        <div className="invalid-feedback d-block">
                          الرابط يجب أن يبدأ بـ https://
                        </div>
                      )}
                    </div>
                  );
                })}

                <div className="form-group mb-3">
                  <label className="form-label d-flex align-items-center" style={{ gap: '8px' }}>
                    <span
                      aria-hidden="true"
                      style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: getPlatformColor('direct'),
                        display: 'inline-block',
                        flexShrink: 0
                      }}
                    />
                    الحجز المباشر
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    dir="ltr"
                    style={{ textAlign: 'left' }}
                    value={formData.features?.directBookingEnabled ? getDirectBookingUrl(propertyId) : 'غير مفعّل'}
                    disabled
                    readOnly
                  />
                  <small className="form-text text-muted">
                    يُنشأ تلقائياً — يُفعّل أو يُوقف من «تفعيل الحجز المباشر» أعلاه.
                  </small>
                </div>
              </>
            )}

            {/* Platform Accounts */}
            <h3 className="form-section-title mt-4">ربط المنصات</h3>

            {/* Section 1: Channex PMS Integration - Global Platforms */}
            {user?.role === 'admin' && (
            <div className="platform-section mb-4">
              <h4 className="platform-title">١ - ربط المنصات العالمية (Airbnb and Booking)</h4>

              {/* Show internal property code in edit mode */}
              {mode === 'edit' && propertyId && (
                <div className="form-group mb-3">
                  <label htmlFor="internal-property-id" className="form-label">رمز العقار</label>
                  <input
                    type="text"
                    id="internal-property-id"
                    value={formData.propertyCode || propertyId}
                    className="form-control"
                    dir="ltr"
                    disabled
                    readOnly
                  />
                  <small className="form-text text-muted">
                    رمز فريد للعقار في النظام (غير قابل للتعديل)
                  </small>
                </div>
              )}

              {/* Show Channex ID if property is in Channex */}
              {mode === 'edit' && formData.channexMapping?.propertyId && (
                <div className="form-group mb-3">
                  <label htmlFor="channex-property-id" className="form-label">معرف العقار في Channex</label>
                  <input
                    type="text"
                    id="channex-property-id"
                    value={formData.channexMapping.propertyId}
                    className="form-control"
                    dir="ltr"
                    disabled
                    readOnly
                  />
                  <small className="form-text text-muted">
                    معرف العقار في نظام Channex للربط مع المنصات
                  </small>
                </div>
              )}

              {/* OTA Credentials Section */}
              {/* Connected Platforms - Read-only display from Channex */}
              <div className="connected-platforms-section mb-4">
                <h5 className="mb-3">المنصات المتصلة</h5>

                {formData.channexMapping?.channels && formData.channexMapping.channels.length > 0 ? (
                  <div className="platforms-list">
                    {formData.channexMapping.channels.map(channel => (
                      <div key={channel.channelId} className="platform-status mb-3 p-3 border rounded">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="mb-1">
                              {channel.platform === 'airbnb' ? 'Airbnb' :
                               channel.platform === 'booking' ? 'Booking.com' :
                               channel.platform === 'expedia' ? 'Expedia' :
                               channel.platform}
                            </h6>
                            <span className={`badge ${channel.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                              {channel.status === 'active' ? 'نشط' : 'غير نشط'}
                            </span>
                            {channel.lastSync && (
                              <small className="text-muted d-block mt-1">
                                آخر مزامنة: {formatCompactDate(channel.lastSync)}
                              </small>
                            )}
                          </div>
                          <i className="bi bi-check-circle text-success" style={{fontSize: '24px'}}></i>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="alert alert-info">
                    <i className="bi bi-info-circle me-2"></i>
                    لا توجد منصات متصلة عبر Channex. استخدم الإجراءات أدناه للربط.
                  </div>
                )}

                {/* Gathern Local OTA */}
                {formData.gathernMapping?.status === 'active' && (
                  <div className="platform-status mb-3 p-3 border rounded">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 className="mb-1">Gathern</h6>
                        <span className="badge bg-success">نشط</span>
                        {formData.gathernMapping.lastSync && (
                          <small className="text-muted d-block mt-1">
                            آخر مزامنة: {formatCompactDate(formData.gathernMapping.lastSync)}
                          </small>
                        )}
                      </div>
                      <i className="bi bi-check-circle text-success" style={{fontSize: '24px'}}></i>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group mb-3">
                <label className="form-label">حالة الربط في Channex</label>
                <div className="mapping-status">
                  <span className={`status-badge ${formData.channexMapping.status}`}>
                    {formData.channexMapping.status === 'active' ? 'مربوط' :
                     formData.channexMapping.status === 'syncing' ? 'جاري المزامنة' :
                     'غير مربوط'}
                  </span>
                  {formData.channexMapping.mappedAt && (
                    <small className="mapping-date">
                      تم الربط: {formatCompactDate(formData.channexMapping.mappedAt)}
                    </small>
                  )}
                </div>
              </div>

              {/* Manual Channex Property ID Input (for multi-unit linking) */}
              {!formData.channexMapping?.propertyId && (
                <div className="form-group mb-3">
                  <label className="form-label">
                    ربط بعقار موجود في Channex (للعقارات المتعددة)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="الصق Channex Property ID هنا"
                    value={formData.channexMapping?.manualPropertyId || ''}
                    onChange={(e) => {
                      const value = e.target.value.trim();
                      setFormData(prev => ({
                        ...prev,
                        channexMapping: {
                          ...prev.channexMapping,
                          manualPropertyId: value,
                          propertyId: value // Set propertyId to trigger room types fetch
                        }
                      }));
                    }}
                  />
                  <small className="form-text text-muted" style={{display: 'block', marginTop: '0.5rem'}}>
                    💡 للعقارات المتعددة في نفس المبنى: الصق Channex Property ID من العقار الأول
                    <br />
                    📝 مثال: 9466b87c-76de-4fb4-8f36-3fc68fb83083
                  </small>
                </div>
              )}

              {/* Multi-Unit Room Type Selection */}
              {formData.channexMapping?.propertyId && (
                <div className="form-group mb-3">
                  <label className="form-label">
                    نوع الغرفة المرتبط (للعقارات المتعددة)
                  </label>
                  <select
                    className="form-control"
                    value={formData.channexMapping?.linkedRoomTypeId || ''}
                    onChange={(e) => {
                      const value = e.target.value;

                      // Just update local state - will be saved when user clicks "حفظ الربط"
                      setFormData(prev => ({
                        ...prev,
                        channexMapping: {
                          ...prev.channexMapping,
                          linkedRoomTypeId: value || null
                        }
                      }));
                    }}
                    disabled={loadingRoomTypes}
                  >
                    <option value="">-- غير محدد (عقار واحد) --</option>
                    {availableRoomTypes.map((roomType) => (
                      <option key={roomType.id} value={roomType.id}>
                        {roomType.attributes?.title || roomType.id}
                      </option>
                    ))}
                  </select>
                  <small className="form-text text-muted" style={{display: 'block', marginTop: '0.5rem'}}>
                    💡 اختر نوع غرفة فقط إذا كان هذا العقار جزء من مبنى متعدد الوحدات
                    <br />
                    (مثال: مبنى به عدة شقق على Booking.com)
                  </small>
                  {loadingRoomTypes && (
                    <small className="text-muted">جاري تحميل أنواع الغرف...</small>
                  )}

                  {/* Save Linking Button - Only show if room type selected */}
                  {formData.channexMapping?.linkedRoomTypeId && mode === 'edit' && (
                    <button
                      type="button"
                      className="btn btn-primary mt-2"
                      onClick={handleSaveLinkedRoomType}
                      disabled={savingLinkedRoomType}
                    >
                      {savingLinkedRoomType ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          جاري حفظ الربط...
                        </>
                      ) : (
                        '💾 حفظ الربط'
                      )}
                    </button>
                  )}
                </div>
              )}

              {/* Channex Actions - Only in edit mode */}
              {mode === 'edit' && propertyId && (
                <div className="channex-actions mb-4">
                  <h5 className="mb-3">إجراءات الربط مع المنصات الخارجية عن طريق Channex</h5>

                  {/* Step-by-Step Channex Setup */}
                  <div className="channex-setup-steps mb-3">
                    {/* Step 1: Create Property in Channex */}
                    <div className="step-item mb-2">
                      <span className="badge bg-secondary me-2">الخطوة 1</span>
                      {!formData.channexMapping?.propertyId ? (
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={async () => {
                            try {
                              setSyncStatus('loading');
                              setSyncMessage('جاري إنشاء العقار في Channex...');

                              const response = await secureApiService.createPropertyInChannex(propertyId);
                              if (response.channexPropertyId) {
                                setSyncStatus('success');
                                setSyncMessage('✅ تم إنشاء العقار في Channex بنجاح');

                                // Fetch updated property data
                                const updatedProperty = await secureApiService.getProperty(propertyId);
                                const primaryOwner = updatedProperty.owners?.find(o => o.isPrimary);

                                setFormData(prev => ({
                                  ...prev,
                                  channexMapping: updatedProperty.channexMapping || prev.channexMapping,
                                  primaryOwnerId: primaryOwner?.userId || prev.primaryOwnerId
                                }));

                                // Clear message after 3 seconds
                                setTimeout(() => {
                                  setSyncStatus('idle');
                                  setSyncMessage('');
                                }, 3000);
                              }
                            } catch (error) {
                              setSyncStatus('error');
                              setSyncMessage('❌ فشل إنشاء العقار في Channex: ' + error.message);

                              // Clear error after 5 seconds
                              setTimeout(() => {
                                setSyncStatus('idle');
                                setSyncMessage('');
                              }, 5000);
                            }
                          }}
                          disabled={syncStatus === 'loading'}
                        >
                          <i className="fas fa-plus-circle me-2"></i>
                          {syncStatus === 'loading' ? 'جاري الإنشاء...' : 'إنشاء العقار في Channex'}
                        </button>
                      ) : (
                        <span className="text-success">
                          <i className="fas fa-check-circle me-2"></i>
                          تم إنشاء العقار ✓
                        </span>
                      )}
                    </div>

                    {/* Step 2: Create Room Types - Always show but conditionally enable */}
                    <div className="step-item mb-2">
                      <span className="badge bg-secondary me-2">الخطوة 2</span>
                      {formData.channexMapping?.propertyId ? (
                        !formData.channexMapping?.roomTypesCreated ? (
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={async () => {
                              try {
                                setSyncStatus('loading');
                                setSyncMessage('جاري إنشاء أنواع الغرف في Channex...');

                                const response = await secureApiService.createRoomTypesInChannex(propertyId);
                                if (response.success) {
                                  setSyncStatus('success');
                                  setSyncMessage('✅ تم إنشاء أنواع الغرف في Channex بنجاح');

                                  // Fetch updated property data
                                  const updatedProperty = await secureApiService.getProperty(propertyId);
                                  setFormData(prev => ({
                                    ...prev,
                                    channexMapping: updatedProperty.channexMapping || prev.channexMapping
                                  }));

                                  // Clear message after 3 seconds
                                  setTimeout(() => {
                                    setSyncStatus('idle');
                                    setSyncMessage('');
                                  }, 3000);
                                }
                              } catch (error) {
                                setSyncStatus('error');
                                setSyncMessage('❌ فشل إنشاء أنواع الغرف: ' + error.message);

                                // Clear error after 5 seconds
                                setTimeout(() => {
                                  setSyncStatus('idle');
                                  setSyncMessage('');
                                }, 5000);
                              }
                            }}
                            disabled={syncStatus === 'loading'}
                          >
                            <i className="fas fa-bed me-2"></i>
                            {syncStatus === 'loading' ? 'جاري الإنشاء...' : 'إنشاء أنواع الغرف'}
                          </button>
                        ) : (
                          <span className="text-success">
                            <i className="fas fa-check-circle me-2"></i>
                            تم إنشاء أنواع الغرف ✓
                          </span>
                        )
                      ) : (
                        <button type="button" className="btn btn-secondary" disabled>
                          <i className="fas fa-bed me-2"></i>
                          إنشاء أنواع الغرف (أكمل الخطوة 1 أولاً)
                        </button>
                      )}
                    </div>

                    {/* Step 3: Create Rate Plan - Always show but conditionally enable */}
                    <div className="step-item mb-2">
                      <span className="badge bg-secondary me-2">الخطوة 3</span>
                      {formData.channexMapping?.propertyId && formData.channexMapping?.roomTypesCreated ? (
                        !formData.channexMapping?.ratePlanCreated ? (
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={async () => {
                              try {
                                setSyncStatus('loading');
                                setSyncMessage('جاري إنشاء خطة الأسعار في Channex...');

                                const response = await secureApiService.createRatePlanInChannex(propertyId);
                                if (response.success) {
                                  setSyncStatus('success');
                                  setSyncMessage('✅ تم إنشاء خطة الأسعار في Channex بنجاح');

                                  // Fetch updated property data
                                  const updatedProperty = await secureApiService.getProperty(propertyId);
                                  setFormData(prev => ({
                                    ...prev,
                                    channexMapping: updatedProperty.channexMapping || prev.channexMapping
                                  }));

                                  // Clear message after 3 seconds
                                  setTimeout(() => {
                                    setSyncStatus('idle');
                                    setSyncMessage('');
                                  }, 3000);
                                }
                              } catch (error) {
                                setSyncStatus('error');
                                setSyncMessage('❌ فشل إنشاء خطة الأسعار: ' + error.message);

                                // Clear error after 5 seconds
                                setTimeout(() => {
                                  setSyncStatus('idle');
                                  setSyncMessage('');
                                }, 5000);
                              }
                            }}
                            disabled={syncStatus === 'loading'}
                          >
                            <i className="fas fa-dollar-sign me-2"></i>
                            {syncStatus === 'loading' ? 'جاري الإنشاء...' : 'إنشاء خطة الأسعار'}
                          </button>
                        ) : (
                          <span className="text-success">
                            <i className="fas fa-check-circle me-2"></i>
                            تم إنشاء خطة الأسعار ✓
                          </span>
                        )
                      ) : (
                        <button type="button" className="btn btn-secondary" disabled>
                          <i className="fas fa-dollar-sign me-2"></i>
                          إنشاء خطة الأسعار (أكمل الخطوة 2 أولاً)
                        </button>
                      )}
                    </div>

                    {/* Step 4: Full Calendar Sync - Push 1 year of availability and rates */}
                    <div className="step-item mb-2">
                      <span className="badge bg-secondary me-2">الخطوة 4</span>
                      {formData.channexMapping?.propertyId && formData.channexMapping?.roomTypesCreated && formData.channexMapping?.ratePlanCreated ? (
                        <div className="sync-configuration">
                          {formData.channexMapping?.initialSyncCompleted ? (
                            // Show completion status if initial sync is done
                            <div className="alert alert-success mb-2">
                              <i className="bi bi-check-circle me-2"></i>
                              <strong>المزامنة الأولية مكتملة</strong>
                              <div className="small mt-1">
                                تمت المزامنة بتاريخ: {formatCompactDate(formData.channexMapping.initialSyncCompletedAt || formData.channexMapping.lastSyncedAt)}
                              </div>
                              <div className="small text-muted mt-1">
                                لتحديث الأسعار، استخدم "تحديث الأسعار المجمع" في صفحة تفاصيل العقار
                              </div>
                            </div>
                          ) : (
                            <>
                              {/* Show if already synced */}
                              {formData.channexMapping?.lastSyncedAt && (
                                <div className="alert alert-info small mb-2">
                                  <i className="bi bi-info-circle me-1"></i>
                                  آخر مزامنة: {formatCompactDate(formData.channexMapping.lastSyncedAt)}
                                </div>
                              )}

                              {/* Configuration inputs */}
                              <div className="row g-2 mb-2">
                                <div className="col-md-6">
                                  <div className="input-group input-group-sm">
                                    <span className="input-group-text">السعر الأساسي</span>
                                    <input
                                      type="number"
                                      className="form-control"
                                      value={baseRate}
                                      onChange={(e) => setBaseRate(e.target.value)}
                                      placeholder="220"
                                      min="1"
                                    />
                                    <span className="input-group-text">ريال</span>
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <div className="input-group input-group-sm">
                                    <span className="input-group-text">زيادة نهاية الأسبوع</span>
                                    <input
                                      type="number"
                                      className="form-control"
                                      value={weekendIncrease}
                                      onChange={(e) => setWeekendIncrease(e.target.value)}
                                      placeholder="30"
                                      min="0"
                                      max="100"
                                    />
                                    <span className="input-group-text">%</span>
                                  </div>
                                </div>
                              </div>

                              <button
                                type="button"
                                className="btn btn-success"
                                onClick={handleFullCalendarSync}
                                disabled={syncStatus === 'loading' || !baseRate}
                              >
                                <i className="bi bi-calendar-check me-2"></i>
                                {syncStatus === 'loading' ? 'جاري المزامنة...' : 'إعداد التقويم الأولي (مرة واحدة)'}
                              </button>
                            </>
                          )}
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          disabled
                        >
                          مزامنة التقويم (أكمل الخطوة 3 أولاً)
                        </button>
                      )}
                      <div className="text-muted small mt-1">
                        تحديث البيانات الكامل - سنة كاملة من التوفر والأسعار والقيود
                      </div>
                    </div>

                    {/* Sync Message */}
                    {syncMessage && (
                      <div className={`alert mt-2 ${syncStatus === 'error' ? 'alert-danger' : syncStatus === 'success' ? 'alert-success' : 'alert-info'}`}>
                        {syncMessage}
                      </div>
                    )}
                  </div>

                  {/* Connect Channels */}
                  {formData.channexMapping?.propertyId && (
                    <>
                      {/* Connect Booking.com */}
                      {formData.otaListings.booking.active && formData.otaListings.booking.propertyId && (
                        <div className="channel-action mb-2">
                          {formData.otaListings.booking.authorizationStatus === 'not_requested' && (
                            <button
                              type="button"
                              className="btn btn-outline-primary me-2"
                              onClick={async () => {
                                try {
                                  const response = await secureApiService.connectChannel(propertyId, 'booking');
                                  if (response.instructions) {
                                    alert('تعليمات الربط:\n' + response.instructions.join('\n'));
                                  }
                                } catch (error) {
                                  alert('فشل طلب الربط: ' + error.message);
                                }
                              }}
                            >
                              <i className="fas fa-link me-2"></i>
                              طلب ربط Booking.com
                            </button>
                          )}

                          {formData.otaListings.booking.authorizationStatus === 'pending' && (
                            <button
                              type="button"
                              className="btn btn-outline-success me-2"
                              onClick={async () => {
                                try {
                                  const response = await secureApiService.verifyChannel(propertyId, 'booking');
                                  if (response.success) {
                                    alert('تم التحقق من الربط بنجاح');
                                    window.location.reload();
                                  }
                                } catch (error) {
                                  alert('فشل التحقق: ' + error.message);
                                }
                              }}
                            >
                              <i className="fas fa-check-circle me-2"></i>
                              التحقق من ربط Booking.com
                            </button>
                          )}
                        </div>
                      )}

                      {/* Connect Airbnb */}
                      {formData.otaListings.airbnb.active && formData.otaListings.airbnb.listingId && (
                        <div className="channel-action mb-2">
                          {formData.otaListings.airbnb.authorizationStatus === 'not_requested' && (
                            <button
                              type="button"
                              className="btn btn-outline-primary me-2"
                              onClick={async () => {
                                try {
                                  const response = await secureApiService.connectChannel(propertyId, 'airbnb');
                                  if (response.instructions) {
                                    alert('تعليمات الربط:\n' + response.instructions.join('\n'));
                                  }
                                } catch (error) {
                                  alert('فشل طلب الربط: ' + error.message);
                                }
                              }}
                            >
                              <i className="fas fa-link me-2"></i>
                              بدء ربط Airbnb
                            </button>
                          )}

                          {formData.otaListings.airbnb.authorizationStatus === 'pending' && (
                            <button
                              type="button"
                              className="btn btn-outline-success me-2"
                              onClick={async () => {
                                try {
                                  const response = await secureApiService.verifyChannel(propertyId, 'airbnb');
                                  if (response.success) {
                                    alert('تم التحقق من الربط بنجاح');
                                    window.location.reload();
                                  }
                                } catch (error) {
                                  alert('فشل التحقق: ' + error.message);
                                }
                              }}
                            >
                              <i className="fas fa-check-circle me-2"></i>
                              التحقق من ربط Airbnb
                            </button>
                          )}
                        </div>
                      )}

                      {/* Quick Onboard All - HIDDEN: Conflicts with new 3-step approach */}
                      {/* Keeping code for reference but not showing to users */}
                      {false && (
                        <button
                          type="button"
                          className="btn btn-warning"
                          onClick={async () => {
                            if (window.confirm('هل تريد محاولة ربط جميع المنصات المتاحة؟')) {
                              try {
                                const response = await secureApiService.quickOnboard(propertyId);
                                alert(`تم الربط: ${response.channels.length} منصات\nأخطاء: ${response.errors.length}`);
                                window.location.reload();
                              } catch (error) {
                                alert('فشل الربط السريع: ' + error.message);
                              }
                            }
                          }}
                        >
                          <i className="fas fa-rocket me-2"></i>
                          ربط سريع لجميع المنصات
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}

            </div>
            )}

            {/* Section 2: Gathern Platform - Local Saudi OTA */}
            <div className="platform-section mb-4">
              <h4 className="platform-title">٢ - ربط المنصات المحلية - Gathern</h4>

              {/* Status Dropdown */}
              <div className="form-group mb-3">
                <label htmlFor="gathern-status" className="form-label">حالة الاتصال</label>
                <select
                  id="gathern-status"
                  value={formData.gathernMapping.status}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    gathernMapping: {
                      ...prev.gathernMapping,
                      status: e.target.value
                    }
                  }))}
                  className="form-control"
                >
                  <option value="pending">قيد الانتظار</option>
                  <option value="active">نشط</option>
                  <option value="inactive">غير نشط</option>
                  <option value="error">خطأ</option>
                </select>
                <small className="form-text text-muted">
                  قم بتعيين الحالة إلى "نشط" لبدء المزامنة التلقائية
                </small>
              </div>

              {/* Property ID */}
              <div className="form-group mb-3">
                <label htmlFor="gathern-propertyId" className="form-label">معرف العقار على Gathern</label>
                <input
                  type="text"
                  id="gathern-propertyId"
                  value={formData.gathernMapping.propertyId}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    gathernMapping: {
                      ...prev.gathernMapping,
                      propertyId: e.target.value
                    }
                  }))}
                  className="form-control"
                  placeholder="مثال: GATH123456"
                />
                <small className="form-text text-muted">
                  اختياري - معرف العقار من لوحة تحكم Gathern
                </small>
              </div>

              {/* Listing Name */}
              <div className="form-group mb-3">
                <label htmlFor="gathern-listing" className="form-label">اسم العقار على Gathern</label>
                <input
                  type="text"
                  id="gathern-listing"
                  value={formData.gathernMapping.listingName}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    gathernMapping: {
                      ...prev.gathernMapping,
                      listingName: e.target.value
                    }
                  }))}
                  className="form-control"
                  placeholder="مثال: شقة مميزة في وسط المدينة"
                />
                <small className="form-text text-muted">
                  يُستخدم لمطابقة الحجوزات من الإيميلات
                </small>
              </div>

              {/* Calendar URL (Import) */}
              <div className="form-group mb-3">
                <label htmlFor="gathern-ical" className="form-label">رابط التقويم من Gathern (Import)</label>
                <input
                  type="url"
                  id="gathern-ical"
                  value={formData.gathernMapping.calendarUrl}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    gathernMapping: {
                      ...prev.gathernMapping,
                      calendarUrl: e.target.value
                    }
                  }))}
                  className="form-control"
                  placeholder="https://gathern.co/calendar/export/..."
                  dir="ltr"
                />
                <small className="form-text text-muted">
                  رابط iCal لاستيراد الحجوزات من Gathern إلى Host Me
                </small>
              </div>

              {/* Export URL (Read-only) */}
              {mode === 'edit' && formData.gathernMapping.exportUrl && (
                <div className="form-group mb-3">
                  <label htmlFor="gathern-export-url" className="form-label">رابط التقويم إلى Gathern (Export)</label>
                  <div className="input-group">
                    <input
                      type="url"
                      id="gathern-export-url"
                      value={formData.gathernMapping.exportUrl}
                      className="form-control"
                      dir="ltr"
                      disabled
                      readOnly
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        navigator.clipboard.writeText(formData.gathernMapping.exportUrl);
                        alert('تم نسخ الرابط!');
                      }}
                    >
                      نسخ
                    </button>
                  </div>
                  <small className="form-text text-muted">
                    استخدم هذا الرابط في Gathern لاستيراد حجوزات من Host Me إلى Gathern (الرابط سيُنشأ تلقائياً بعد إضافة العقار)
                  </small>
                </div>
              )}

              {/* Sync Metadata */}
              {mode === 'edit' && formData.gathernMapping.lastSync && (
                <div className="alert alert-info">
                  <strong>معلومات المزامنة:</strong>
                  <ul className="mb-0 mt-2">
                    <li>آخر مزامنة: {formatCompactDate(formData.gathernMapping.lastSync)}</li>
                    <li>الحجوزات المستوردة: {formData.gathernMapping.syncMetadata.lastImportedBookings || 0}</li>
                    {formData.gathernMapping.lastSyncError && (
                      <li className="text-danger">آخر خطأ: {formData.gathernMapping.lastSyncError}</li>
                    )}
                    {formData.gathernMapping.syncMetadata.importErrorCount > 0 && (
                      <li className="text-warning">
                        عدد الأخطاء: {formData.gathernMapping.syncMetadata.importErrorCount}
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* Action Buttons */}
              {mode === 'edit' && formData.gathernMapping.calendarUrl && (
                <div className="d-flex gap-2 mb-3">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={async () => {
                      try {
                        setSyncStatus('loading');
                        const response = await secureApiService.testGathernConnection(propertyId);
                        if (response.success) {
                          alert(`✅ تم الاتصال بنجاح!\nتم العثور على ${response.bookingsCount || 0} حجز`);
                        } else {
                          alert('❌ فشل الاتصال: ' + (response.error || 'خطأ غير معروف'));
                        }
                        setSyncStatus('idle');
                      } catch (error) {
                        alert('❌ خطأ في الاتصال: ' + error.message);
                        setSyncStatus('idle');
                      }
                    }}
                    disabled={syncStatus === 'loading'}
                  >
                    <i className="bi bi-wifi me-1"></i>
                    اختبار الاتصال
                  </button>

                  {/* Admin only: the endpoint behind this button re-syncs EVERY property of
                      EVERY tenant, not just this one, so it is not an owner-scoped action.
                      Backend enforces the same rule (syncRoutes.js) — this just avoids
                      showing a DIY owner a button that would 403. */}
                  {user?.role === 'admin' && (
                  <button
                    type="button"
                    className="btn btn-outline-success"
                    onClick={async () => {
                      try {
                        setSyncStatus('loading');
                        setSyncMessage('جاري المزامنة مع Gathern...');
                        const response = await secureApiService.syncPropertyNow(propertyId);
                        if (response.success) {
                          alert(`✅ تمت المزامنة بنجاح!\nتم استيراد ${response.bookingsImported || 0} حجز`);
                          window.location.reload();
                        } else {
                          alert('⚠️ المزامنة فشلت: ' + (response.error || 'خطأ غير معروف'));
                        }
                        setSyncStatus('idle');
                        setSyncMessage('');
                      } catch (error) {
                        alert('❌ خطأ في المزامنة: ' + error.message);
                        setSyncStatus('idle');
                        setSyncMessage('');
                      }
                    }}
                    disabled={syncStatus === 'loading' || formData.gathernMapping.status !== 'active'}
                  >
                    <i className="bi bi-arrow-repeat me-1"></i>
                    {syncStatus === 'loading' ? 'جاري المزامنة...' : 'مزامنة الآن'}
                  </button>
                  )}
                </div>
              )}
            </div>

            {/* Export Calendar Section - REMOVED: Duplicate of export URL in Gathern section above */}

            {/* Sync Status Indicator */}
            {syncStatus !== 'idle' && (
              <div className={`alert ${
                syncStatus === 'syncing' ? 'alert-info' : 
                syncStatus === 'success' ? 'alert-success' : 'alert-warning'
              } mb-3`}>
                <div className="d-flex align-items-center">
                  {syncStatus === 'syncing' && (
                    <>
                      <i className="bi bi-arrow-repeat spin me-2"></i>
                      جاري مزامنة التغييرات مع النظام المتقدم...
                    </>
                  )}
                  {syncStatus === 'success' && (
                    <>
                      <i className="bi bi-check-circle-fill me-2"></i>
                      تم تحديث اسم العقار في النظام المتقدم بنجاح
                    </>
                  )}
                  {syncStatus === 'error' && (
                    <>
                      <i className="bi bi-exclamation-circle-fill me-2"></i>
                      تم حفظ العقار محلياً، ولكن فشل في تحديث النظام المتقدم
                    </>
                  )}
                </div>
              </div>
            )}
              </div>
            )}

            {/* Tab 2: Public Booking Page Content */}
            {activeTab === 'publicContent' && (
              <div className="public-content-tab" dir="rtl">
                <h3 className="form-section-title mb-4">محتوى الصفحة العامة</h3>

                {/* Public catalogue placement (tasks/public-listings-page.md).
                    Edit-mode only, like the public booking links: a property being created is not
                    ready to advertise, and publishing is a deliberate marketing act — it is never
                    derived from the unit being operationally active. */}
                {mode === 'edit' && propertyId && (
                  <div className="mb-4">
                    <h5 className="mb-3">صفحة الوحدات العامة</h5>

                    <div className="form-group mb-3">
                      <div className="form-check form-switch">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="publicListingIsPublished"
                          checked={formData.publicListing?.isPublished || false}
                          onChange={(e) => setFormData({
                            ...formData,
                            publicListing: {
                              ...formData.publicListing,
                              isPublished: e.target.checked
                            }
                          })}
                          style={{ cursor: 'pointer' }}
                        />
                        <label className="form-check-label" htmlFor="publicListingIsPublished" style={{ cursor: 'pointer' }}>
                          <strong>عرض الوحدة في صفحة الوحدات العامة</strong>
                          <br />
                          <small className="text-muted">
                            الصفحة العامة التي نشاركها على إنستغرام وواتساب.
                          </small>
                        </label>
                      </div>

                      {/* ONLY WHAT IS MISSING. The conditions the public endpoint actually applies —
                          ticking the switch while a required one is unmet produces a unit that never
                          appears on the page, and the only trace is a server log nobody reads. But a
                          list of four green ticks on a unit that is already fine is noise, and this
                          tab is long enough. So a ready unit gets one line and everything else is
                          hidden; a unit with a gap gets exactly the gap. */}
                      {publishChecklist.some(({ met }) => !met) ? (
                        <ul className="list-unstyled mt-3 mb-0" style={{ fontSize: '0.86rem' }}>
                          {publishChecklist.filter(({ met }) => !met).map(({ key, label, required }) => (
                            <li key={key} className="d-flex align-items-start" style={{ gap: '8px', marginBottom: '5px' }}>
                              <span aria-hidden="true" style={{ color: required ? '#dc3545' : '#997404', lineHeight: 1.6 }}>
                                {required ? '✗' : '!'}
                              </span>
                              <span>
                                {label}
                                <span className="text-muted"> {required ? '— مطلوب للعرض' : '— مستحسن'}</span>
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="mt-3 mb-0" style={{ fontSize: '0.86rem', color: '#198754' }}>
                          ✓ الوحدة مكتملة وجاهزة للعرض
                        </p>
                      )}
                    </div>

                    {/* Deliberately `type="text"` with an inputMode, and deliberately no
                        min/max/step: a native constraint attribute makes the browser block the
                        WHOLE form with its own locale's bubble, so the Arabic message the backend
                        was given («ترتيب العرض يجب أن يكون رقماً صحيحاً بين 0 و 9999») is never the
                        one anyone reads. One owner of the message — the pre-submit check below,
                        beside the booking-link check that already works this way.
                        The raw string is kept in state so clearing the field stays cleared rather
                        than snapping to 0 mid-edit and turning the next keystroke into "012". */}
                    <div className="form-group mb-3">
                      <label className="form-label" htmlFor="publicListingSortOrder">ترتيب العرض</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        id="publicListingSortOrder"
                        className={`form-control${isValidSortOrder(formData.publicListing?.sortOrder) ? '' : ' is-invalid'}`}
                        style={{ maxWidth: '160px', textAlign: 'right' }}
                        value={formData.publicListing?.sortOrder ?? ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          publicListing: {
                            ...formData.publicListing,
                            sortOrder: e.target.value
                          }
                        })}
                      />
                      {!isValidSortOrder(formData.publicListing?.sortOrder) && (
                        <div className="invalid-feedback d-block">
                          رقم صحيح بين 0 و 9999
                        </div>
                      )}
                      <small className="text-muted d-block mt-1">
                        الأصغر يظهر أولاً. اتركه فارغاً إن لم يكن للترتيب أهمية.
                      </small>
                    </div>
                  </div>
                )}

                {/* Description & Photos Section */}
                <div className="mb-4">
                  <h5 className="mb-3">الوصف والصور</h5>

                  {/* The guest-facing headline. Falls back to the property name server-side when
                      empty — but several units share a near-identical name, so a catalogue built on
                      the fallback shows indistinguishable cards. Ask for it here.

                      ⚠️ EDIT MODE ONLY, and not for tidiness: the CREATE route never destructures
                      `bookingPageSettings`, so anything typed here while creating a property is
                      dropped on the floor and the operator is still shown «تم إنشاء العقار». Rather
                      than widen the create route for one field, the whole public-content block is
                      an edit-time concern — the same rule the public booking links already follow. */}
                  {mode === 'edit' && propertyId && (
                  <div className="form-group mb-3">
                    <label className="form-label" htmlFor="publicTitle">العنوان العام للوحدة</label>
                    <input
                      type="text"
                      id="publicTitle"
                      className="form-control"
                      maxLength={120}
                      style={{ textAlign: 'right' }}
                      placeholder={formData.name || 'مثال: شقة غرفتين بإطلالة على البحر — أبحر الشمالية'}
                      value={formData.bookingPageSettings?.publicTitle || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        bookingPageSettings: {
                          ...formData.bookingPageSettings,
                          publicTitle: e.target.value
                        }
                      })}
                    />
                    <small className="text-muted d-block mt-1">
                      العنوان الذي يراه الضيف. إن تُرك فارغاً يُستخدم اسم العقار — وهو اسم تشغيلي متشابه
                      بين عدة وحدات، لذا يُفضّل كتابة عنوان مميز.{' '}
                      {/* Counted through `formatProgress`, not hand-written: `120/8` inside Arabic
                          copy is one LTR run and an Arabic reader meets the total first. */}
                      ({formatProgress((formData.bookingPageSettings?.publicTitle || '').length, 120, 'ar')})
                    </small>
                  </div>
                  )}

                  <div className="form-group mb-3">
                    <label className="form-label">الصورة الرئيسية</label>
                    <div style={{ position: 'relative', width: '200px', height: '150px', border: '2px dashed #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                      {formData.bookingPageSettings?.primaryPhoto ? (
                        <>
                          <img
                            src={formData.bookingPageSettings.primaryPhoto}
                            alt="الصورة الرئيسية"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                          <label
                            htmlFor="primary-photo-upload"
                            style={{
                              position: 'absolute',
                              top: '5px',
                              left: '5px',
                              cursor: 'pointer',
                              backgroundColor: 'rgba(255,255,255,0.9)',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}
                          >
                            <i className="bi bi-cloud-upload"></i> رفع
                          </label>
                        </>
                      ) : (
                        <label
                          htmlFor="primary-photo-upload"
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                            cursor: 'pointer',
                            color: '#6c757d'
                          }}
                        >
                          {uploadingImage ? (
                            <>
                              <i className="bi bi-arrow-clockwise spin" style={{ fontSize: '24px' }}></i>
                              <span style={{ fontSize: '12px', marginTop: '8px' }}>جاري الرفع...</span>
                            </>
                          ) : (
                            <>
                              <i className="bi bi-cloud-upload" style={{ fontSize: '24px' }}></i>
                              <span style={{ fontSize: '12px', marginTop: '8px' }}>رفع صورة</span>
                            </>
                          )}
                        </label>
                      )}
                      <input
                        type="file"
                        id="primary-photo-upload"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            handleImageUpload(file, 'primary');
                          }
                          e.target.value = '';
                        }}
                        disabled={uploadingImage}
                      />
                    </div>
                  </div>

                  {/* Gallery Photos */}
                  <div className="form-group mb-3">
                    <label className="form-label">معرض الصور</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                      {(formData.bookingPageSettings?.gallery || []).map((photo, index) => (
                        <div
                          key={index}
                          style={{
                            position: 'relative',
                            width: '150px',
                            height: '110px',
                            border: '2px dashed #ddd',
                            borderRadius: '8px',
                            overflow: 'hidden'
                          }}
                        >
                          {photo ? (
                            <>
                              <img
                                src={photo}
                                alt={`صورة ${index + 1}`}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                              <label
                                htmlFor={`gallery-photo-upload-${index}`}
                                style={{
                                  position: 'absolute',
                                  top: '5px',
                                  left: '5px',
                                  cursor: 'pointer',
                                  backgroundColor: 'rgba(255,255,255,0.9)',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '11px'
                                }}
                              >
                                <i className="bi bi-cloud-upload"></i>
                              </label>
                              <button
                                type="button"
                                style={{
                                  position: 'absolute',
                                  top: '5px',
                                  right: '5px',
                                  cursor: 'pointer',
                                  backgroundColor: 'rgba(220,53,69,0.9)',
                                  color: 'white',
                                  border: 'none',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '11px'
                                }}
                                onClick={async () => {
                                  const gallery = [...(formData.bookingPageSettings?.gallery || [])];
                                  gallery.splice(index, 1);

                                  const updatedBookingPageSettings = {
                                    ...formData.bookingPageSettings,
                                    gallery
                                  };

                                  // Update UI state
                                  setFormData({
                                    ...formData,
                                    bookingPageSettings: updatedBookingPageSettings
                                  });

                                  // Persist immediately to MongoDB (only in edit mode)
                                  if (propertyId && mode === 'edit') {
                                    const requestId = `img-delete-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                                    const imageUrlToDelete = photo;

                                    try {
                                      // Step 1: Delete physical file from S3/local storage
                                      const token = localStorage.getItem('token');
                                      await axios.delete(
                                        `${process.env.REACT_APP_API_BASE_URL}/api/v1/upload/property-image`,
                                        {
                                          data: {
                                            imageUrl: imageUrlToDelete,
                                            propertyId: propertyId
                                          },
                                          headers: {
                                            'x-auth-token': token,
                                            'Content-Type': 'application/json'
                                          }
                                        }
                                      );

                                      // Step 2: Update MongoDB — send the whole nested object
                                      // (dotted paths are silently dropped by the PUT handler)
                                      await secureApiService.updateProperty(propertyId, {
                                        bookingPageSettings: updatedBookingPageSettings
                                      });

                                      // Update originalProperty to reflect saved state (for change detection)
                                      setOriginalProperty(prev => ({
                                        ...prev,
                                        bookingPageSettings: updatedBookingPageSettings
                                      }));

                                      alert('✅ تم حذف الصورة بنجاح');
                                    } catch (error) {
                                      console.error(`[${requestId}] ❌ Error deleting image:`, error);
                                      alert('❌ فشل حذف الصورة. يرجى المحاولة مرة أخرى.');
                                    }
                                  }
                                }}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </>
                          ) : (
                            <label
                              htmlFor={`gallery-photo-upload-${index}`}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                                height: '100%',
                                cursor: 'pointer',
                                color: '#6c757d'
                              }}
                            >
                              <i className="bi bi-cloud-upload" style={{ fontSize: '20px' }}></i>
                              <span style={{ fontSize: '11px', marginTop: '4px' }}>رفع</span>
                            </label>
                          )}
                          <input
                            type="file"
                            id={`gallery-photo-upload-${index}`}
                            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                handleImageUpload(file, 'gallery', index);
                              }
                              e.target.value = '';
                            }}
                            disabled={uploadingImage}
                          />
                        </div>
                      ))}
                      <div
                        style={{
                          width: '150px',
                          height: '110px',
                          border: '2px dashed #6c757d',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          color: '#6c757d'
                        }}
                        onClick={() => {
                          const gallery = [...(formData.bookingPageSettings?.gallery || []), ''];
                          setFormData({
                            ...formData,
                            bookingPageSettings: {
                              ...formData.bookingPageSettings,
                              gallery
                            }
                          });
                        }}
                      >
                        <div style={{ textAlign: 'center' }}>
                          <i className="bi bi-plus-circle" style={{ fontSize: '24px' }}></i>
                          <div style={{ fontSize: '12px', marginTop: '4px' }}>إضافة صورة</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="form-group mb-3">
                    <label className="form-label">الوصف (عربي)</label>
                    <textarea
                      className="form-control"
                      rows="4"
                      placeholder="أدخل وصف العقار باللغة العربية..."
                      value={formData.bookingPageSettings?.description || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        bookingPageSettings: {
                          ...formData.bookingPageSettings,
                          description: e.target.value
                        }
                      })}
                      dir="rtl"
                      style={{ textAlign: 'right' }}
                    />
                  </div>
                </div>

                {/* Booking & Cancellation Section */}
                <div className="mb-4">
                  <h5 className="mb-3">شروط الحجز والإلغاء</h5>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">وقت تسجيل الوصول</label>
                      <input
                        type="time"
                        className="form-control"
                        value={formData.bookingPageSettings?.checkInTime || '15:00'}
                        onChange={(e) => setFormData({
                          ...formData,
                          bookingPageSettings: {
                            ...formData.bookingPageSettings,
                            checkInTime: e.target.value
                          }
                        })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">وقت تسجيل المغادرة</label>
                      <input
                        type="time"
                        className="form-control"
                        value={formData.bookingPageSettings?.checkOutTime || '11:00'}
                        onChange={(e) => setFormData({
                          ...formData,
                          bookingPageSettings: {
                            ...formData.bookingPageSettings,
                            checkOutTime: e.target.value
                          }
                        })}
                      />
                    </div>
                  </div>

                  <div className="form-group mb-3">
                    <label className="form-label">قواعد المنزل (عربي)</label>
                    <textarea
                      className="form-control"
                      rows="4"
                      placeholder="أدخل قواعد المنزل (كل قاعدة في سطر منفصل)..."
                      value={formData.bookingPageSettings?.houseRules?.join('\n') || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        bookingPageSettings: {
                          ...formData.bookingPageSettings,
                          houseRules: e.target.value.split('\n').filter(r => r.trim())
                        }
                      })}
                      dir="rtl"
                      style={{ textAlign: 'right' }}
                    />
                  </div>

                  <div className="form-group mb-3">
                    <label className="form-label">سياسة الإلغاء</label>
                    <select
                      className="form-control"
                      value={formData.bookingPageSettings?.cancellationPolicy?.type || 'moderate'}
                      onChange={(e) => setFormData({
                        ...formData,
                        bookingPageSettings: {
                          ...formData.bookingPageSettings,
                          cancellationPolicy: {
                            ...formData.bookingPageSettings?.cancellationPolicy,
                            type: e.target.value
                          }
                        }
                      })}
                    >
                      <option value="flexible">مرنة</option>
                      <option value="moderate">متوسطة</option>
                      <option value="strict">صارمة</option>
                    </select>
                    <small className="text-muted d-block mt-1" style={{ fontSize: '12px', textAlign: 'right' }}>
                      مرنة: استرداد كامل قبل 24 ساعة • متوسطة: قبل 5 أيام • صارمة: قبل 14 يوم
                    </small>
                  </div>
                </div>

                {/* Important Information Section */}
                <div className="mb-4">
                  <h5 className="mb-3">معلومات مهمة</h5>
                  <div className="form-group mb-3">
                    <label className="form-label">معلومات مهمة للضيوف (عربي)</label>
                    <textarea
                      className="form-control"
                      rows="4"
                      placeholder="مثال: وصف مختصر يظهر في الصفحة العامة أو ملاحظات لخدمة الضيوف قبل الحجز..."
                      value={formData.bookingPageSettings?.importantInformation || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        bookingPageSettings: {
                          ...formData.bookingPageSettings,
                          importantInformation: e.target.value
                        }
                      })}
                      dir="rtl"
                      style={{ textAlign: 'right' }}
                    />
                  </div>
                </div>

                {/* ── Amenities: what the unit HAS ──────────────────────────────────────────
                     tasks/public-amenities-consolidation.md D2. Rendered from
                     `config/amenityCatalogue.js`, not hand-written: this block was ~250 lines of
                     copy-pasted checkboxes for 9 amenities, which is exactly why nobody added the
                     10th. Adding one now is a line in the catalogue.

                     Headed «المرافق والتجهيزات», deliberately NOT «المرافق والخدمات» — the guide
                     tab owns that second heading. Two identical headings on two tabs is the
                     confusion this whole change exists to remove. */}
                <div className="mb-4">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                    <h5 className="mb-0">المرافق والتجهيزات</h5>
                    {mode === 'edit' && (
                      <div className="d-flex align-items-center gap-2">
                        <select
                          className="form-select form-select-sm"
                          style={{ minWidth: '200px' }}
                          value={copySourceId}
                          onChange={(e) => setCopySourceId(e.target.value)}
                          onFocus={loadCopySources}
                          disabled={copyBusy}
                        >
                          <option value="">نسخ المرافق من وحدة أخرى…</option>
                          {copySources.map((p) => (
                            <option key={p._id} value={p._id}>{p.name}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          disabled={!copySourceId || copyBusy}
                          onClick={copyAmenitiesFrom}
                        >
                          {copyBusy ? '...' : 'نسخ'}
                        </button>
                      </div>
                    )}
                  </div>

                  <p className="text-muted small mb-3">
                    ما تحتويه الوحدة فعلياً. أما تعليمات الاستخدام — كلمة مرور الواي فاي، مكان الموقف،
                    أوقات المسبح — فتُكتب في تبويب «دليل العقار».
                  </p>

                  {AMENITY_GROUPS.map((group) => (
                    <div key={group.key} className="mb-3 p-3 border rounded">
                      <div className="fw-bold mb-2">{group.label}</div>
                      <div className="row">
                        {group.items.map((item) => {
                          const checked = !!readAmenity(formData.amenities, item.path);
                          const inputId = `amenity-${item.path.replace(/\./g, '-')}`;
                          return (
                            <div className="col-md-4 mb-2" key={item.path}>
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={inputId}
                                  checked={checked}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    amenities: writeAmenity(formData.amenities, item.path, e.target.checked)
                                  })}
                                />
                                <label className="form-check-label" htmlFor={inputId}>{item.label}</label>
                              </div>

                              {/* Detail controls appear only once the amenity itself is ticked —
                                  a parking type on a unit with no parking is noise, and saving one
                                  would leave a stale value behind if the box is later unticked. */}
                              {checked && item.detail === AMENITY_DETAILS.PARKING_TYPE && (
                                <select
                                  className="form-select form-select-sm mt-1"
                                  value={formData.amenities?.parking?.type || ''}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    amenities: writeAmenity(formData.amenities, 'parking.type', e.target.value)
                                  })}
                                >
                                  <option value="">نوع الموقف…</option>
                                  {PARKING_TYPES.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                  ))}
                                </select>
                              )}

                              {checked && item.detail === AMENITY_DETAILS.KITCHEN_TYPE && (
                                <select
                                  className="form-select form-select-sm mt-1"
                                  value={formData.amenities?.kitchen?.type || ''}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    amenities: writeAmenity(formData.amenities, 'kitchen.type', e.target.value)
                                  })}
                                >
                                  <option value="">نوع المطبخ…</option>
                                  {KITCHEN_TYPES.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                  ))}
                                </select>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

              </div>
            )}

            {/* Tab 3: Guidebook */}
            {activeTab === 'guidebook' && (
              <div className="guidebook-tab" dir="rtl">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h3 className="form-section-title mb-0">دليل العقار</h3>
                  {mode === 'edit' && (
                    <small className={`text-${guidebookSaveStatus === 'saved' ? 'success' : guidebookSaveStatus === 'error' ? 'danger' : 'muted'}`}>
                      {guidebookSaveStatus === 'saving' && (
                        <>
                          <i className="bi bi-arrow-clockwise spin me-1"></i>
                          جاري الحفظ...
                        </>
                      )}
                      {guidebookSaveStatus === 'saved' && (
                        <>
                          <i className="bi bi-check-circle me-1"></i>
                          تم الحفظ
                        </>
                      )}
                      {guidebookSaveStatus === 'error' && (
                        <>
                          <i className="bi bi-exclamation-circle me-1"></i>
                          خطأ في الحفظ
                        </>
                      )}
                      {/* Not an error — an edit that is not finished yet. Distinct from «خطأ في
                          الحفظ» on purpose: nothing failed, and nothing was lost. */}
                      {guidebookSaveStatus === 'unsaved' && (
                        <>
                          <i className="bi bi-pencil me-1"></i>
                          لم يُحفظ — أكمل الحقل
                        </>
                      )}
                    </small>
                  )}
                </div>

                <div className="alert alert-info mb-4">
                  <i className="bi bi-info-circle me-2"></i>
                  تُستخدم بيانات دليل العقار (تعليمات الدخول، الواي فاي) في رسالة تسجيل الدخول التلقائية التي تُرسل للضيف قبل موعد الوصول، وكذلك للإجابة على الأسئلة الشائعة. يمكنك لاحقاً طباعته أو مشاركته كملف PDF.
                </div>

                {/* The guest-guide link first: it is what the guest receives and what the printed
                    sheet's QR points to (tasks/guest-guide-sections-and-print-sheet.md). */}
                {mode === 'edit' && propertyId && (
                  <GuestGuideLinkCard link={arrivalGuideLink} onRegenerate={handleArrivalGuideRegenerate} />
                )}

                <div className="guidebook-section" style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
                  <h5 style={{ marginBottom: '10px' }}>الفيديو التعريفي بالوحدة</h5>

                  <div style={{ marginBottom: '12px' }}>
                    <input
                      type="text"
                      className="form-control"
                      value={guidebookSections.onboardingVideo || ''}
                      placeholder="https://youtu.be/ABC123 أو https://www.youtube.com/watch?v=ABC123"
                      style={{ direction: 'ltr', textAlign: 'left' }}
                      onChange={(e) => {
                        const videoUrl = e.target.value.trim();
                        setFormData((prev) => {
                          const updatedGuidebook = {
                            ...prev.guidebook,
                            sections: {
                              ...prev.guidebook.sections,
                              onboardingVideo: videoUrl
                            }
                          };
                          autoSaveGuidebook(updatedGuidebook);
                          return {
                            ...prev,
                            guidebook: updatedGuidebook
                          };
                        });
                      }}
                    />
                  </div>

                  {guidebookSections.onboardingVideo && (() => {
                    const videoUrl = guidebookSections.onboardingVideo;
                    const isYouTube = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/.test(videoUrl);

                    if (isYouTube) {
                      // Extract YouTube video ID and create embed URL
                      let videoId;
                      if (videoUrl.includes('youtu.be/')) {
                        videoId = videoUrl.split('youtu.be/')[1]?.split(/[?&]/)[0];
                      } else if (videoUrl.includes('shorts/')) {
                        videoId = videoUrl.split('shorts/')[1]?.split(/[?&]/)[0];
                      } else if (videoUrl.includes('watch?v=')) {
                        videoId = videoUrl.split('watch?v=')[1]?.split('&')[0];
                      }

                      return (
                        <div style={{ marginTop: '15px' }}>
                          <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: '8px', overflow: 'hidden' }}>
                            <iframe
                              src={`https://www.youtube.com/embed/${videoId}`}
                              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              title="Property Onboarding Video"
                            />
                          </div>
                          <div style={{ fontSize: '0.85rem', marginTop: '8px', color: '#28a745' }}>
                            ✅ فيديو YouTube - جاهز للإرسال للضيوف
                          </div>
                        </div>
                      );
                    } else {
                      // Fallback for non-YouTube URLs (legacy S3 videos)
                      return (
                        <div style={{ marginTop: '15px' }}>
                          <video
                            src={videoUrl}
                            controls
                            preload="metadata"
                            style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }}
                          />
                          <div style={{ fontSize: '0.85rem', marginTop: '8px', color: '#ff9800' }}>
                            ⚠️ فيديو غير مستضاف على YouTube - يُفضل استخدام YouTube للحصول على أفضل أداء
                          </div>
                        </div>
                      );
                    }
                  })()}
                </div>

                <div className="card p-3 mb-4">
                  <h5 className="mb-3">تعليمات الدخول</h5>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">طريقة الدخول</label>
                      <select
                        className="form-select"
                        value={guidebookSections.arrival.entryType}
                        onChange={(e) => handleGuidebookSectionChange('arrival', 'entryType', e.target.value)}
                      >
                        <option value="smartlock">لوحة ذكية / رمز</option>
                        <option value="keybox">صندوق مفاتيح</option>
                        <option value="reception">استقبال</option>
                        <option value="concierge">خدمة الكونسيرج</option>
                        <option value="self_checkin">تسجيل دخول ذاتي</option>
                        <option value="staff">حارس / موظف</option>
                      </select>
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">رمز الدخول</label>
                      {guidebookSections.arrival.entryType === 'smartlock' ? (
                        // Smart-lock code is per-booking (Booking.accessCode), generated + sent
                        // automatically. Hiding this static field prevents a stale code being stored.
                        <small className="text-muted">
                          <i className="bi bi-info-circle me-2"></i>
                          رمز القفل الذكي يُنشأ تلقائياً لكل حجز ويُرسل في رسالة تسجيل الدخول — لا حاجة لإدخاله هنا.
                        </small>
                      ) : (
                        <>
                          <input
                            type="text"
                            className="form-control"
                            value={guidebookSections.arrival.entryCode}
                            onChange={(e) => handleGuidebookSectionChange('arrival', 'entryCode', e.target.value)}
                            maxLength={100}
                          />
                          <small className="text-muted">رمز ثابت على مستوى العقار (لصندوق المفاتيح ونحوه)</small>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">تعليمات الدخول (عربي)</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={guidebookSections.arrival.entryInstructionsAr}
                        onChange={(e) => handleGuidebookSectionChange('arrival', 'entryInstructionsAr', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6 mb-3" dir="ltr">
                      <label className="form-label">Entry Instructions (English - اختياري)</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={guidebookSections.arrival.entryInstructionsEn}
                        onChange={(e) => handleGuidebookSectionChange('arrival', 'entryInstructionsEn', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">موقع المفتاح (عربي)</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={guidebookSections.arrival.keyLocationAr}
                        onChange={(e) => handleGuidebookSectionChange('arrival', 'keyLocationAr', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6 mb-3" dir="ltr">
                      <label className="form-label">Key Location (English - اختياري)</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={guidebookSections.arrival.keyLocationEn}
                        onChange={(e) => handleGuidebookSectionChange('arrival', 'keyLocationEn', e.target.value)}
                      />
                    </div>
                  </div>
                  {/*
                    Structured early check-in (D153). Replaced two free-text boxes — one Arabic, one
                    English — that between them held «حسب التوافر والإمكانية» on 4 of 12 apartments
                    and nothing at all on the other 8. That is not a policy, and no system can act on
                    it. Plan: tasks/structured-early-checkin-policy.md
                    The old placeholder also suggested a FEE; pricing is out of scope here (D159).
                  */}
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">{EARLY_CHECKIN_FIELD_LABEL}</label>
                      <select
                        className="form-select"
                        value={earlyCheckInPolicy}
                        onChange={(e) => handleGuidebookSectionChange('arrival', 'earlyCheckInPolicy', e.target.value)}
                      >
                        {EARLY_CHECKIN_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <small className="text-muted">{EARLY_CHECKIN_HELP[earlyCheckInPolicy]}</small>
                    </div>
                    {earlyCheckInPolicy === 'NOT_BEFORE' && (
                      <div className="col-md-4 mb-3">
                        <label className="form-label">{EARLY_CHECKIN_TIME_LABEL}</label>
                        <input
                          type="time"
                          className={`form-control${earlyCheckInTimeError ? ' is-invalid' : ''}`}
                          value={guidebookSections.arrival.earlyCheckInNotBefore || ''}
                          onChange={(e) => handleGuidebookSectionChange('arrival', 'earlyCheckInNotBefore', e.target.value)}
                        />
                        {earlyCheckInTimeError ? (
                          <div className="invalid-feedback d-block">{earlyCheckInTimeError}</div>
                        ) : (
                          <small className="text-muted">
                            {/* formatTime + bdi: the hour is western digits inside Arabic RTL copy,
                                and the repo's wall-clock formatter is the sanctioned way to render
                                it. Without bdi the bracketed time reorders against the sentence. */}
                            يجب أن يكون قبل وقت تسجيل الوصول (<bdi>{formatTime(propertyCheckInTime)}</bdi>)
                          </small>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="row">
                    <div className="col-12 mb-3">
                      <small className="text-muted">
                        <i className="bi bi-info-circle me-2"></i>
                        {EARLY_CHECKIN_POLICY_NOT_STATE_AR}
                      </small>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">سياسة تأخير المغادرة (عربي)</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        placeholder="مثال: تأخير المغادرة متاح حتى 6 مساءً - حتى 2 ظهراً مجاني - من 2 ظهراً إلى 6 مساءً: 150 ريال"
                        value={guidebookSections.arrival.lateCheckOutPolicyAr}
                        onChange={(e) => handleGuidebookSectionChange('arrival', 'lateCheckOutPolicyAr', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6 mb-3" dir="ltr">
                      <label className="form-label">Late Check-out Policy (English - اختياري)</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        placeholder="Example: Late checkout available until 6 PM - Until 2 PM: Free - 2 PM to 6 PM: 150 SAR"
                        value={guidebookSections.arrival.lateCheckOutPolicyEn}
                        onChange={(e) => handleGuidebookSectionChange('arrival', 'lateCheckOutPolicyEn', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* ====== دليل الوصول — the ordered photo journey (tasks/unit-arrival-guide-page.md) ======
                    Its OWN card, deliberately not folded into «الواي فاي والمواقف» below: those
                    parking text fields keep their existing job (AI-assistant facts) and the journey
                    here runs well past parking — car → bay → gate → entrance → lift → door (D3/D4).
                    Edit mode only, because an upload needs a propertyId to attach to. */}
                {mode === 'edit' && propertyId && (
                  <div className="card p-3 mb-4">
                    <h5 className="mb-3">دليل الوصول</h5>

                    <p className="text-muted small mb-3">
                      صور مرتّبة تشرح للضيف طريق الوصول خطوة بخطوة: الموقف، جهاز البوابة، مدخل
                      المبنى، الدور، ثم باب الشقة. التعليقات مكتوبة داخل الصور بالعربية والإنجليزية،
                      فلا حاجة لكتابة نص هنا. الترتيب من اليمين هو ترتيب الخطوات.
                    </p>

                    {arrivalGuideImages.length > 0 ? (
                      <div className="d-flex gap-3 flex-wrap mb-2">
                        {arrivalGuideImages.map((image, index) => (
                          <div key={image} style={{ width: '150px' }}>
                            <div style={{ position: 'relative' }}>
                              <img
                                src={image}
                                alt={`خطوة الوصول ${index + 1}`}
                                style={{
                                  width: '150px', height: '110px', objectFit: 'cover',
                                  borderRadius: '8px', border: '1px solid #dee2e6', background: '#f1f3f5'
                                }}
                              />
                              <span
                                style={{
                                  position: 'absolute', top: '4px', insetInlineStart: '4px',
                                  background: 'rgba(71,19,150,0.92)', color: 'white',
                                  borderRadius: '50%', width: '22px', height: '22px',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: '12px', fontWeight: 'bold'
                                }}
                              >
                                {index + 1}
                              </span>
                              <button
                                type="button"
                                title="حذف الصورة"
                                onClick={() => handleArrivalGuideRemove(index)}
                                style={{
                                  position: 'absolute', top: '4px', insetInlineEnd: '4px',
                                  background: 'rgba(220,53,69,0.9)', color: 'white', border: 'none',
                                  padding: '2px 6px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer'
                                }}
                              >
                                ✕
                              </button>
                            </div>
                            {/* Previous/next, not drag-and-drop — ordering is the point of the
                                feature, but nine items do not earn a DnD dependency. The row reads
                                right-to-left, so the PREVIOUS step sits to the right; chevron
                                glyphs are not auto-mirrored, hence the deliberate pairing. */}
                            <div className="d-flex justify-content-center gap-1 mt-1">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary py-0 px-2"
                                title="نقل إلى الخطوة السابقة"
                                disabled={index === 0}
                                onClick={() => handleArrivalGuideMove(index, -1)}
                              >
                                <i className="bi bi-chevron-right"></i>
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary py-0 px-2"
                                title="نقل إلى الخطوة التالية"
                                disabled={index === arrivalGuideImages.length - 1}
                                onClick={() => handleArrivalGuideMove(index, +1)}
                              >
                                <i className="bi bi-chevron-left"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted small mb-2">لا توجد صور بعد.</p>
                    )}

                    {arrivalGuideImages.length < ARRIVAL_GUIDE_MAX && (
                      <div>
                        <label
                          htmlFor="arrival-guide-upload"
                          className="btn btn-outline-primary btn-sm"
                          style={{ cursor: uploadingArrivalGuide ? 'wait' : 'pointer' }}
                        >
                          {uploadingArrivalGuide ? 'جارٍ الرفع…' : '+ إضافة صورة'}
                        </label>
                        <input
                          type="file"
                          id="arrival-guide-upload"
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          style={{ display: 'none' }}
                          disabled={uploadingArrivalGuide}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) handleArrivalGuideUpload(file);
                            e.target.value = '';
                          }}
                        />
                      </div>
                    )}

                    <small className="text-muted d-block mt-2">
                      {arrivalGuideImages.length} من {ARRIVAL_GUIDE_MAX} صورة · تُحفظ تلقائياً بعد
                      لحظات من التعديل — راقب حالة الحفظ أعلى الصفحة.
                    </small>

                  </div>
                )}

                <div className="card p-3 mb-4">
                  <h5 className="mb-3 d-flex align-items-center gap-2 flex-wrap">
                    <span>الواي فاي والمواقف</span>
                    {/* Badge only — these fields are deliberately NOT gated by
                        `amenities.wifi.available`. The automated check-in message reads
                        `guidebook.sections.wifi` live, so an unticked box on the other tab must
                        never be able to hide the credentials it sends
                        (tasks/public-amenities-consolidation.md D3). */}
                    {formData.amenities?.wifi?.available ? (
                      <span className="badge bg-light text-secondary fw-normal">واي فاي: متاح</span>
                    ) : (
                      <span className="badge bg-warning text-dark fw-normal">واي فاي غير مُحدَّد في «الصفحة العامة»</span>
                    )}
                  </h5>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">اسم الشبكة</label>
                      <input
                        type="text"
                        className="form-control"
                        value={guidebookSections.wifi.networkName}
                        onChange={(e) => handleGuidebookSectionChange('wifi', 'networkName', e.target.value)}
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">كلمة المرور</label>
                      <input
                        type="text"
                        className="form-control"
                        value={guidebookSections.wifi.password}
                        onChange={(e) => handleGuidebookSectionChange('wifi', 'password', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">ملاحظات الواي فاي (عربي)</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={guidebookSections.wifi.instructionsAr}
                        onChange={(e) => handleGuidebookSectionChange('wifi', 'instructionsAr', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6 mb-3" dir="ltr">
                      <label className="form-label">WiFi Notes (English - اختياري)</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={guidebookSections.wifi.instructionsEn}
                        onChange={(e) => handleGuidebookSectionChange('wifi', 'instructionsEn', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <AmenityGuideField
                      label="معلومات المواقف (عربي)"
                      amenityPath="parking.available"
                      amenities={formData.amenities}
                      value={guidebookSections.parking.descriptionAr}
                      className="col-md-6"
                    >
                      <textarea
                        className="form-control"
                        rows="2"
                        value={guidebookSections.parking.descriptionAr}
                        onChange={(e) => handleGuidebookSectionChange('parking', 'descriptionAr', e.target.value)}
                      />
                    </AmenityGuideField>
                    <AmenityGuideField
                      label="Parking Notes (English - اختياري)"
                      amenityPath="parking.available"
                      amenities={formData.amenities}
                      value={guidebookSections.parking.descriptionEn}
                      className="col-md-6"
                    >
                      <textarea
                        className="form-control"
                        rows="2" dir="ltr"
                        value={guidebookSections.parking.descriptionEn}
                        onChange={(e) => handleGuidebookSectionChange('parking', 'descriptionEn', e.target.value)}
                      />
                    </AmenityGuideField>
                  </div>
                </div>

                <div className="card p-3 mb-4">
                  <h5 className="mb-3">التكييف والأجهزة</h5>
                  {anyAmenityFieldVisible(formData.amenities, [
                    ['airConditioning', guidebookSections.climate.acInstructionsAr],
                    ['heating', guidebookSections.climate.heaterInstructionsAr],
                    ['kitchen.available', guidebookSections.appliances.kitchenNotesAr],
                    ['washingMachine', guidebookSections.appliances.washerNotesAr],
                    ['tv', guidebookSections.appliances.tvNotesAr],
                    ['airConditioning', guidebookSections.climate.acInstructionsEn],
                    ['heating', guidebookSections.climate.heaterInstructionsEn],
                    ['kitchen.available', guidebookSections.appliances.kitchenNotesEn],
                    ['washingMachine', guidebookSections.appliances.washerNotesEn],
                    ['tv', guidebookSections.appliances.tvNotesEn]
                  ]) ? (
                  <>
                  <div className="row">
                    <AmenityGuideField
                      label={t('guestGuide.unit.acAr')}
                      amenityPath="airConditioning"
                      amenities={formData.amenities}
                      value={guidebookSections.climate.acInstructionsAr}
                      className="col-md-6 mb-3"
                    >
                      <textarea
                        className="form-control"
                        rows="2"
                        value={guidebookSections.climate.acInstructionsAr}
                        onChange={(e) => handleGuidebookSectionChange('climate', 'acInstructionsAr', e.target.value)}
                      />
                    </AmenityGuideField>
                    <AmenityGuideField
                      label={t('guestGuide.unit.acEn')}
                      amenityPath="airConditioning"
                      amenities={formData.amenities}
                      value={guidebookSections.climate.acInstructionsEn}
                      className="col-md-6 mb-3"
                    >
                      <textarea
                        className="form-control"
                        rows="2" dir="ltr"
                        value={guidebookSections.climate.acInstructionsEn}
                        onChange={(e) => handleGuidebookSectionChange('climate', 'acInstructionsEn', e.target.value)}
                      />
                    </AmenityGuideField>
                    <AmenityGuideField
                      label={t('guestGuide.unit.heaterAr')}
                      amenityPath="heating"
                      amenities={formData.amenities}
                      value={guidebookSections.climate.heaterInstructionsAr}
                      className="col-md-6 mb-3"
                    >
                      <textarea
                        className="form-control"
                        rows="2"
                        value={guidebookSections.climate.heaterInstructionsAr}
                        onChange={(e) => handleGuidebookSectionChange('climate', 'heaterInstructionsAr', e.target.value)}
                      />
                    </AmenityGuideField>
                    <AmenityGuideField
                      label={t('guestGuide.unit.heaterEn')}
                      amenityPath="heating"
                      amenities={formData.amenities}
                      value={guidebookSections.climate.heaterInstructionsEn}
                      className="col-md-6 mb-3"
                    >
                      <textarea
                        className="form-control"
                        rows="2" dir="ltr"
                        value={guidebookSections.climate.heaterInstructionsEn}
                        onChange={(e) => handleGuidebookSectionChange('climate', 'heaterInstructionsEn', e.target.value)}
                      />
                    </AmenityGuideField>
                  </div>
                  <div className="row">
                    <AmenityGuideField
                      label="المطبخ / الأجهزة"
                      amenityPath="kitchen.available"
                      amenities={formData.amenities}
                      value={guidebookSections.appliances.kitchenNotesAr}
                      className="col-md-6 mb-3"
                    >
                      <textarea
                        className="form-control"
                        rows="2"
                        value={guidebookSections.appliances.kitchenNotesAr}
                        onChange={(e) => handleGuidebookSectionChange('appliances', 'kitchenNotesAr', e.target.value)}
                      />
                    </AmenityGuideField>
                    <AmenityGuideField
                      label={t('guestGuide.unit.kitchenEn')}
                      amenityPath="kitchen.available"
                      amenities={formData.amenities}
                      value={guidebookSections.appliances.kitchenNotesEn}
                      className="col-md-6 mb-3"
                    >
                      <textarea
                        className="form-control"
                        rows="2" dir="ltr"
                        value={guidebookSections.appliances.kitchenNotesEn}
                        onChange={(e) => handleGuidebookSectionChange('appliances', 'kitchenNotesEn', e.target.value)}
                      />
                    </AmenityGuideField>
                    <AmenityGuideField
                      label="الغسالة / النشافة"
                      amenityPath="washingMachine"
                      amenities={formData.amenities}
                      value={guidebookSections.appliances.washerNotesAr}
                      className="col-md-6 mb-3"
                    >
                      <textarea
                        className="form-control"
                        rows="2"
                        value={guidebookSections.appliances.washerNotesAr}
                        onChange={(e) => handleGuidebookSectionChange('appliances', 'washerNotesAr', e.target.value)}
                      />
                    </AmenityGuideField>
                    <AmenityGuideField
                      label={t('guestGuide.unit.washerEn')}
                      amenityPath="washingMachine"
                      amenities={formData.amenities}
                      value={guidebookSections.appliances.washerNotesEn}
                      className="col-md-6 mb-3"
                    >
                      <textarea
                        className="form-control"
                        rows="2" dir="ltr"
                        value={guidebookSections.appliances.washerNotesEn}
                        onChange={(e) => handleGuidebookSectionChange('appliances', 'washerNotesEn', e.target.value)}
                      />
                    </AmenityGuideField>
                    <AmenityGuideField
                      label="التلفزيون / الرسيفر"
                      amenityPath="tv"
                      amenities={formData.amenities}
                      value={guidebookSections.appliances.tvNotesAr}
                      className="col-md-6 mb-3"
                    >
                      <textarea
                        className="form-control"
                        rows="2"
                        value={guidebookSections.appliances.tvNotesAr}
                        onChange={(e) => handleGuidebookSectionChange('appliances', 'tvNotesAr', e.target.value)}
                      />
                    </AmenityGuideField>
                    <AmenityGuideField
                      label={t('guestGuide.unit.tvEn')}
                      amenityPath="tv"
                      amenities={formData.amenities}
                      value={guidebookSections.appliances.tvNotesEn}
                      className="col-md-6 mb-3"
                    >
                      <textarea
                        className="form-control"
                        rows="2" dir="ltr"
                        value={guidebookSections.appliances.tvNotesEn}
                        onChange={(e) => handleGuidebookSectionChange('appliances', 'tvNotesEn', e.target.value)}
                      />
                    </AmenityGuideField>
                  </div>
                  </>
                  ) : (
                    <p className="text-muted small mb-0">
                      لم تُحدَّد أجهزة لهذه الوحدة. فعّل المكيف أو التدفئة أو المطبخ أو الغسالة أو
                      التلفزيون في تبويب «الصفحة العامة» لتظهر حقول التعليمات هنا.
                    </p>
                  )}
                </div>

                {/* Guest-guide content (tasks/guest-guide-sections-and-print-sheet.md Phase 2):
                    where the bins are — differs by building — and the unit's optional own rules. */}
                <div className="card p-3 mb-4">
                  <h5 className="mb-3">{t('guestGuide.unit.waste')}</h5>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label" htmlFor="guide-waste-ar">{t('guestGuide.unit.wasteAr')}</label>
                      <textarea
                        id="guide-waste-ar"
                        className="form-control"
                        rows="2"
                        value={guidebookSections.waste.descriptionAr}
                        onChange={(e) => handleGuidebookSectionChange('waste', 'descriptionAr', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label d-block" htmlFor="guide-waste-en" dir="auto">{t('guestGuide.unit.wasteEn')}</label>
                      <textarea
                        id="guide-waste-en"
                        className="form-control"
                        rows="2" dir="ltr"
                        value={guidebookSections.waste.descriptionEn}
                        onChange={(e) => handleGuidebookSectionChange('waste', 'descriptionEn', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <UnitHouseRulesCard
                  value={guidebookSections.houseRulesOverride}
                  onChange={(field, lines) => handleGuidebookSectionChange('houseRulesOverride', field, lines)}
                />

                <div className="card p-3 mb-4">
                  <h5 className="mb-3">المرافق والإضافات</h5>
                  <div className="row">
                    <AmenityGuideField
                      label="المسبح"
                      amenityPath="pool.available"
                      amenities={formData.amenities}
                      value={guidebookSections.amenities.poolInfoAr}
                    >
                      <textarea
                        className="form-control"
                        rows="2"
                        value={guidebookSections.amenities.poolInfoAr}
                        onChange={(e) => handleGuidebookSectionChange('amenities', 'poolInfoAr', e.target.value)}
                      />
                    </AmenityGuideField>
                    <AmenityGuideField
                      label="الصالة الرياضية"
                      amenityPath="gym.available"
                      amenities={formData.amenities}
                      value={guidebookSections.amenities.gymInfoAr}
                    >
                      <textarea
                        className="form-control"
                        rows="2"
                        value={guidebookSections.amenities.gymInfoAr}
                        onChange={(e) => handleGuidebookSectionChange('amenities', 'gymInfoAr', e.target.value)}
                      />
                    </AmenityGuideField>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">تجهيزات العائلات</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={guidebookSections.amenities.familyInfoAr}
                        onChange={(e) => handleGuidebookSectionChange('amenities', 'familyInfoAr', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">أسرة إضافية</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={guidebookSections.extras.extraBedsAr}
                        onChange={(e) => handleGuidebookSectionChange('extras', 'extraBedsAr', e.target.value)}
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">المفروشات والمناشف</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={guidebookSections.extras.linensAr}
                        onChange={(e) => handleGuidebookSectionChange('extras', 'linensAr', e.target.value)}
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">مستلزمات إضافية</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={guidebookSections.extras.suppliesAr}
                        onChange={(e) => handleGuidebookSectionChange('extras', 'suppliesAr', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="card p-3 mb-4">
                  <h5 className="mb-3">السلامة والمواقع القريبة</h5>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">أرقام الطوارئ / جهات الاتصال</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={guidebookSections.safety.emergencyContacts}
                        onChange={(e) => handleGuidebookSectionChange('safety', 'emergencyContacts', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">ملاحظات السلامة</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={guidebookSections.safety.safetyNotesAr}
                        onChange={(e) => handleGuidebookSectionChange('safety', 'safetyNotesAr', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-3 mb-3">
                      <label className="form-label">السوبرماركت</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={guidebookSections.localTips.supermarketsAr}
                        onChange={(e) => handleGuidebookSectionChange('localTips', 'supermarketsAr', e.target.value)}
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label">المطاعم</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={guidebookSections.localTips.restaurantsAr}
                        onChange={(e) => handleGuidebookSectionChange('localTips', 'restaurantsAr', e.target.value)}
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label">المساجد</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={guidebookSections.localTips.mosquesAr}
                        onChange={(e) => handleGuidebookSectionChange('localTips', 'mosquesAr', e.target.value)}
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label">خدمات التوصيل</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={guidebookSections.localTips.deliveryAppsAr}
                        onChange={(e) => handleGuidebookSectionChange('localTips', 'deliveryAppsAr', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="card p-3 mb-4">
                  <h5 className="mb-3">ملاحظات إضافية</h5>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">ملاحظات (عربي)</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={guidebookSections.customNotes.notesAr}
                        onChange={(e) => handleGuidebookSectionChange('customNotes', 'notesAr', e.target.value)}
                      />
                    </div>
                    <div className="col-md-6 mb-3" dir="ltr">
                      <label className="form-label">Notes (English - اختياري)</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={guidebookSections.customNotes.notesEn}
                        onChange={(e) => handleGuidebookSectionChange('customNotes', 'notesEn', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-check mb-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="guidebookPublished"
                    checked={formData.guidebook?.isPublished || false}
                    onChange={(e) => handleGuidebookPublishToggle(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="guidebookPublished">
                    نشر الدليل (جعله متاحاً لمساعد الضيوف الذكي)
                  </label>
                </div>
              </div>
            )}

            <div className="form-actions">
              {!isGuidebookOnly && (<>
              <button
                type="submit"
                className="btn btn-primary me-2"
                disabled={creating}
              >
                {creating ? (
                  <>
                    <i className="bi bi-arrow-clockwise spin"></i>
                    {mode === 'edit' ? 'جاري التحديث...' : 'جاري الإنشاء...'}
                  </>
                ) : (
                  <>
                    <i className={mode === 'edit' ? 'bi bi-check-circle' : 'bi bi-plus-circle'}></i>
                    {mode === 'edit' ? 'تحديث العقار' : 'إنشاء العقار'}
                  </>
                )}
              </button>
              
              {mode === 'edit' && (
                <button 
                  type="button" 
                  className="btn btn-danger me-2"
                  onClick={() => setShowDeleteModal(true)}
                  disabled={creating || deleting}
                >
                  <i className="bi bi-trash"></i>
                  حذف العقار
                </button>
              )}
              </>)}

              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
              >
                إلغاء
              </button>
            </div>
          </form>
        </div>
        
        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">تأكيد حذف العقار</h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setShowDeleteModal(false)}
                    disabled={deleting}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="d-flex align-items-center mb-3">
                    <i className="bi bi-exclamation-triangle text-warning fs-2 me-3"></i>
                    <div>
                      <h6 className="mb-1">هل أنت متأكد من حذف هذا العقار؟</h6>
                      <p className="mb-0 text-muted">
                        سيتم حذف العقار "{formData.name}" وجميع البيانات المرتبطة به نهائياً ولا يمكن التراجع عن هذا الإجراء.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setShowDeleteModal(false)}
                    disabled={deleting}
                  >
                    إلغاء
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-danger"
                    onClick={handleDeleteProperty}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <>
                        <i className="bi bi-arrow-clockwise spin me-1"></i>
                        جاري الحذف...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-trash me-1"></i>
                        نعم، احذف العقار
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Modal backdrop */}
        {showDeleteModal && (
          <div 
            className="modal-backdrop fade show" 
            onClick={() => !deleting && setShowDeleteModal(false)}
          ></div>
        )}
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="properties-error" dir="rtl">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }
  
  return (
    <div className="properties-management" dir="rtl">
        <div className="header-actions">
          <Link to="/dashboard" className="back-to-dashboard-btn">
            <i className="bi bi-arrow-right"></i> العودة إلى لوحة التحكم
          </Link>
        </div>

        {/* Account Switcher - For admin/ops only */}
        {(user?.role === 'admin' || user?.role === 'ops') && (
          <div style={{ marginBottom: '1.5rem' }}>
            <AccountSwitcher />
          </div>
        )}

        {/* Status Filter - For admin/ops only */}
        {(user?.role === 'admin' || user?.role === 'ops') && (
          <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label htmlFor="statusFilter" style={{ fontWeight: 'bold', marginBottom: 0 }}>
              حالة العقار:
            </label>
            <select
              id="statusFilter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #ddd',
                fontSize: '14px',
                minWidth: '150px'
              }}
            >
              <option value="active">نشط فقط</option>
              <option value="all">جميع الحالات</option>
              <option value="deleted">محذوف فقط</option>
            </select>
            <span style={{ color: '#666', fontSize: '13px' }}>
              ({properties.length} عقار)
            </span>
          </div>
        )}

      <div className="properties-list">
        {properties.length === 0 ? (
          <div className="no-properties">
            <p>لا توجد عقارات لعرضها</p>
            {user?.role === 'admin' && (
              <Link to="/properties/add" className="btn btn-primary">
                إضافة عقار جديد
              </Link>
            )}
          </div>
        ) : (
          properties.map(property => (
            <div key={property._id} className={`property-card ${property.deletedAt ? 'deleted' : ''}`}>
              <div className="property-card-header">
                <div className="property-card-identity">
                  <PropertyThumb
                    src={property.bookingPageSettings?.primaryPhoto}
                    name={property.name}
                  />
                  <h3>
                    {property.name}
                    {property.propertyCode && (
                      <span className="badge bg-light text-dark ms-2 fw-normal" style={{ fontSize: '0.55em', verticalAlign: 'middle' }} dir="ltr">{property.propertyCode}</span>
                    )}
                  </h3>
                </div>
                <div className="status-badges">
                  {/* Listing status: only show as an exception (inactive/maintenance/deleted) — hide when active to reduce noise */}
                  {(property.deletedAt || (property.status && property.status !== 'active')) && (
                    <span className={`property-status ${property.deletedAt ? 'deleted' : property.status}`}>
                      {formatStatus(property)}
                    </span>
                  )}
                  {/* «حالة التشغيل» — derived, one batched call for the whole list (tasks/unit-operational-status.md S4). */}
                  <UnitStatusChip unitStatus={unitStatuses[String(property._id)]} />
                </div>
              </div>

              <div className="property-card-details">
                {property.address && (
                  <p><i className="bi bi-geo-alt"></i> {property.address}</p>
                )}
                {property.type && (
                  <p><i className="bi bi-house"></i> {property.type}</p>
                )}
                
                {/* Platforms/OTAs - Display customer-facing platforms only */}
                {(property.channexMapping?.channels || property.gathernMapping) && (
                  <div className="platforms-info">
                    <span className="platforms-label">المنصات النشطة:</span>

                    {/* Global OTAs via Channex - use channexMapping.channels (source of truth) */}
                    {property.channexMapping?.channels?.filter(ch => ch.status === 'active').map(channel => (
                      <span key={channel.channelId} className={`platform-badge ${channel.platform}`}>
                        {channel.platform === 'airbnb' ? 'Airbnb' :
                         channel.platform === 'booking' ? 'Booking.com' :
                         channel.platform === 'expedia' ? 'Expedia' :
                         channel.platform}
                      </span>
                    ))}

                    {/* Local Saudi OTA */}
                    {property.gathernMapping?.status === 'active' && (
                      <span className="platform-badge gathern">Gathern</span>
                    )}
                  </div>
                )}
                
                {/* Bookings count */}
                {property.bookings && (
                  <p><i className="bi bi-calendar-check"></i> {property.bookings.length} حجوزات</p>
                )}
                
                {/* Owner Info - Show for admin only */}
                {user?.role === 'admin' && property.owners && property.owners.length > 0 && (() => {
                  const primaryOwner = property.owners.find(o => o.isPrimary);
                  const ownerData = primaryOwner?.userId;

                  if (ownerData) {
                    return (
                      <div className="property-owner">
                        <div>
                          <span className="owner-label">المالك: </span>
                          <span className="owner-name">
                            {ownerData.name || ownerData.email}
                          </span>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
              
              <div className="property-card-footer">
                <Link to={`/properties/${property._id}`} className="view-details-btn">
                  <i className="bi bi-eye"></i> عرض التفاصيل
                </Link>
                
                {/* Edit button - only for admins and DIY owners */}
                {user?.role === 'admin' && (
                  <Link to={`/properties/${property._id}/edit`} className="edit-property-btn">
                    <i className="bi bi-pencil"></i> تعديل
                  </Link>
                )}
                {/* Ops reach the same page in guidebook-only mode (G11). */}
                {(user?.role === 'ops' || user?.role === 'ops-manager') && (
                  <Link to={`/properties/${property._id}/edit`} className="edit-property-btn">
                    <i className="bi bi-journal-text"></i> {t('guestGuide.unit.guidebookLink')}
                  </Link>
                )}
              </div>
            </div>
          ))
        )}
        
        {/* Add Property button at bottom - only for Admin and DIY owners */}
        {properties.length > 0 && user?.role === 'admin' && (
          <div className="add-property-bottom">
            <Link to="/properties/add" className="add-property-btn-bottom">
              <i className="bi bi-plus-circle"></i> إضافة عقار جديد
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecurePropertiesManagement;
